// Firestore service for event CRUD operations
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  deleteDoc,
  deleteField,
  increment,
  runTransaction,
  limit,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Chess } from 'chess.js';

// Collection reference
const EVENTS_COLLECTION = 'events';

/**
 * Create a new event in Firestore
 * @param {Object} eventData - Event data object
 * @param {string} organizerId - User ID of the event organizer
 * @returns {Promise<string>} - Returns the created event ID
 */
export const createEvent = async (eventData, organizerId) => {
  try {
    // Add timestamps and organizer ID
    const eventWithTimestamps = {
      ...eventData,
      organizerId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'draft', // Default status
      isPublished: false,
      // Allow admin support access when admin explicitly creates this item
      adminCanAccess: eventData.adminCreated === true ? true : false,
      participants: {} // Initialize empty participants map
    };

    // Create event with auto-generated ID
    const eventsCollection = collection(db, EVENTS_COLLECTION);
    const docRef = await addDoc(eventsCollection, eventWithTimestamps);
    
    return docRef.id;
  } catch (error) {
    throw error;
  }
};

/**
 * Create event with custom ID
 * @param {string} eventId - Custom event ID
 * @param {Object} eventData - Event data object
 * @param {string} organizerId - User ID of the event organizer
 * @returns {Promise<string>} - Returns the event ID
 */
export const createEventWithId = async (eventId, eventData, organizerId) => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    const userRef = doc(db, USERS_COLLECTION, organizerId);
    
    // Fetch existing event to preserve participants if present
    let existingParticipants = {};
    const existingSnap = await getDoc(eventRef);
    const isNewEvent = !existingSnap.exists();
    
    if (existingSnap.exists()) {
      const existingData = existingSnap.data();
      if (existingData && existingData.participants) {
        existingParticipants = existingData.participants;
      }
    }
    
    // Add timestamps and organizer ID
    const eventWithTimestamps = {
      ...eventData,
      eventId,
      organizerId,
      createdAt: isNewEvent ? serverTimestamp() : existingSnap.data().createdAt,
      updatedAt: serverTimestamp(),
      status: 'draft', // Default status
      isPublished: false,
      // Preserve existing admin access or set when admin is creating
      adminCanAccess: eventData.adminCreated === true ? true : (existingSnap.exists() && existingSnap.data().adminCanAccess === true ? true : false),
      participants: existingParticipants // Preserve participants
    };
    
    // Create or update event with custom ID
    await setDoc(eventRef, eventWithTimestamps);
    
    // If this is a new event, increment the user's event counter
    if (isNewEvent) {
      try {
        await updateDoc(userRef, {
          eventsCreated: increment(1),
          updatedAt: serverTimestamp()
        });
      } catch (userUpdateError) {
        // Don't throw - event creation should still succeed even if counter fails
      }
    }
    
    return eventId;
  } catch (error) {
    throw error;
  }
};

/**
 * Get an event by ID
 * @param {string} eventId - Event ID
 * @returns {Promise<Object|null>} - Returns event data or null if not found
 */
export const getEvent = async (eventId) => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    const eventSnap = await getDoc(eventRef);
    
    if (eventSnap.exists()) {
      const eventData = eventSnap.data();
      return {
        id: eventSnap.id,
        ...eventData
      };
    } else {
      return null;
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Get all events (with optional filtering)
 * @param {Object} filters - Optional filters (e.g., { status: 'published' })
 * @returns {Promise<Array>} - Returns array of events
 */
export const getEvents = async (filters = {}) => {
  try {
    const eventsCollection = collection(db, EVENTS_COLLECTION);
    let q = eventsCollection;

    // Apply filters if provided
    if (filters.status) {
      q = query(eventsCollection, where('status', '==', filters.status));
    }

    const querySnapshot = await getDocs(q);
    const events = [];

    querySnapshot.forEach((doc) => {
      events.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return events;
  } catch (error) {
    throw error;
  }
};

/**
 * Update an existing event
 * @param {string} eventId - Event ID
 * @param {Object} updatedData - Updated event data
 * @returns {Promise<void>}
 */
export const updateEvent = async (eventId, updatedData) => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    
    // Add updated timestamp
    const dataWithTimestamp = {
      ...updatedData,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(eventRef, dataWithTimestamp);
  } catch (error) {
    throw error;
  }
};

/**
 * Publish an event (change status to published)
 * @param {string} eventId - Event ID
 * @returns {Promise<void>}
 */
export const publishEvent = async (eventId) => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    await updateDoc(eventRef, {
      status: 'published',
      isPublished: true,
      publishedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Check if an event exists
 * @param {string} eventId - Event ID
 * @returns {Promise<boolean>} - Returns true if event exists
 */
export const eventExists = async (eventId) => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    const eventSnap = await getDoc(eventRef);
    return eventSnap.exists();
  } catch (error) {
    throw error;
  }
};

/**
 * Duplicate an event with selective field preservation
 * Creates a new tournament using settings from an existing tournament as a template
 * @param {string} sourceEventId - Original event ID to duplicate from
 * @param {string} organizerId - User ID creating the duplicate
 * @param {Object} newEventData - New event data (title, dates, etc.)
 * @returns {Promise<string>} - Returns the new event ID
 */
export const duplicateEvent = async (sourceEventId, organizerId, newEventData) => {
  try {
    // console.log('duplicateEvent called with:', { sourceEventId, organizerId, newEventData });

    // Fetch the source event
    const sourceEvent = await getEvent(sourceEventId);
    // console.log('Source event fetched:', sourceEvent ? 'Found' : 'Not found');

    if (!sourceEvent) {
      throw new Error('Source event not found');
    }

    // Fields to MAINTAIN (copy from original)
    const maintainedFields = {
      // Stripe Connect settings
      stripeAccountId: sourceEvent.stripeAccountId,
      stripeConnected: sourceEvent.stripeConnected,
      stripeConnectCompleted: sourceEvent.stripeConnectCompleted,

      // Ownership (preserve co-owner if exists)
      owner2Id: sourceEvent.owner2Id,
      adminCanAccess: sourceEvent.adminCanAccess,

      // Payment settings
      cashPaymentsEnabled: sourceEvent.cashPaymentsEnabled,
      sponsorshipEnabled: sourceEvent.sponsorshipEnabled,
      partialSponsorshipEnabled: sourceEvent.partialSponsorshipEnabled || false,
      partialSponsorshipAmount: sourceEvent.partialSponsorshipAmount || 10,
      currency: sourceEvent.currency,
      feeOverride: sourceEvent.feeOverride,
      paymentInfo: sourceEvent.paymentInfo,

      // Registration configuration
      enableRegistration: sourceEvent.enableRegistration,
      registrationType: sourceEvent.registrationType,
      fieldSettings: sourceEvent.fieldSettings,

      // Tournament structure
      tournamentSections: sourceEvent.tournamentSections,
      enablePlayUp: sourceEvent.enablePlayUp,
      playUpFee: sourceEvent.playUpFee,
      playUpDescription: sourceEvent.playUpDescription,
      tournamentFormat: sourceEvent.tournamentFormat,
      timeControl: sourceEvent.timeControl,
      maxPlayers: sourceEvent.maxPlayers,

      // Federation settings
      federation: sourceEvent.federation,
      federationType: sourceEvent.federationType,
      selectedFederations: sourceEvent.selectedFederations,
      customFederation: sourceEvent.customFederation,

      // Design
      primaryColorHex: sourceEvent.primaryColorHex,
      eventLogo: sourceEvent.eventLogo,

      // Tab visibility settings
      sections: sourceEvent.sections,

      // Tab custom modes
      tabCustomModes: sourceEvent.tabCustomModes,

      // Contact info templates (as defaults)
      directorName: sourceEvent.directorName,
      directorEmail: sourceEvent.directorEmail,
      directorPhone: sourceEvent.directorPhone,
      organizerName: sourceEvent.organizerName,
      organizerEmail: sourceEvent.organizerEmail,
      organizerPhone: sourceEvent.organizerPhone,
      registrationName: sourceEvent.registrationName,
      registrationEmail: sourceEvent.registrationEmail,
      registrationPhone: sourceEvent.registrationPhone,
      websiteUrl: sourceEvent.websiteUrl,

      // Instructions templates
      registrationInstructions: sourceEvent.registrationInstructions,
      scheduleInstructions: sourceEvent.scheduleInstructions,

      // Home page content
      homeOrganizerSection: sourceEvent.homeOrganizerSection,

      // Custom tabs structure (preserve labels, reset content)
      customTabs: sourceEvent.customTabs ? sourceEvent.customTabs.map(tab => ({
        id: Date.now() + Math.random(), // New IDs
        label: tab.label,
        content: '' // Reset content
      })) : [],

      // Team settings
      isTeamEvent: sourceEvent.isTeamEvent,
      teamSize: sourceEvent.teamSize,
      allowIndividualRegistration: sourceEvent.allowIndividualRegistration,
    };

    // Fields to RESET (cleared or set to defaults)
    const resetFields = {
      // Event identity (override with newEventData)
      title: newEventData.title || 'Copy of ' + (sourceEvent.title || sourceEvent.eventTitle || 'Tournament'),
      eventTitle: newEventData.title || 'Copy of ' + (sourceEvent.title || sourceEvent.eventTitle || 'Tournament'),
      eventDescription: '',
      announcement: '',

      // Dates (override with newEventData)
      startDate: newEventData.startDate || '',
      endDate: newEventData.endDate || newEventData.startDate || '',
      isSingleDay: newEventData.isSingleDay !== undefined ? newEventData.isSingleDay : true,

      // Location
      venueName: '',
      venueAddress: '',
      city: '',
      location: '',
      checkinTime: '',

      // Participants and revenue
      participants: {},
      lastParticipantUpdate: null,

      // Status
      status: 'draft',
      isPublished: false,
      publishedAt: null,

      // Content sections
      customLocationContent: '',
      customContactContent: '',
      customRulesContent: '',
      customRegistrationContent: '',
      customFAQContent: '',
      customResultsContent: '',
      customParticipantsContent: '',
      customScheduleContent: '',
      customAboutContent: '',

      // Custom sections (reset all content)
      homeCustomSections: [],
      aboutCustomSections: [],
      locationCustomSections: [],
      contactInfoCustomSections: [],
      rulesCustomSections: [],
      faqCustomSections: [],
      scheduleCustomSections: [],
      resultsCustomSections: [],

      // Schedule and results
      rounds: [],
      resultsLink: '',
      scheduleLink: '',

      // FAQ and rules
      faqContent: '',
      rules: '',

      // Recurring event settings
      isRecurring: false,
      recurrenceType: 'weekly',
      weeklyDayOfWeek: 1,
      monthlyPattern: 'dayOfMonth',
      monthlyDayOfMonth: 1,
      monthlyNthWeekday: { nth: 1, dayOfWeek: 1 },
      recurrenceStartDate: '',
      recurrenceEndDate: '',
      cancelledDates: [],
      rescheduledDates: [],

      // Onboarding
      onboardingCompleted: true, // Skip onboarding for duplicated events
    };

    // Combine maintained and reset fields
    const duplicatedEventData = {
      ...maintainedFields,
      ...resetFields,
    };

    // Filter out undefined values (Firestore doesn't allow undefined)
    const cleanedEventData = Object.entries(duplicatedEventData).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});

    // console.log('Creating new event with duplicated data...');
    // console.log('Sample of duplicated data:', {
    //   title: cleanedEventData.title,
    //   startDate: cleanedEventData.startDate,
    //   hasStripeAccount: !!cleanedEventData.stripeAccountId,
    //   hasSections: !!cleanedEventData.tournamentSections,
    //   fieldCount: Object.keys(cleanedEventData).length
    // });

    // Create the new event
    const newEventId = await createEvent(cleanedEventData, organizerId);

    // console.log('New event created successfully with ID:', newEventId);
    return newEventId;
  } catch (error) {
    console.error('Error duplicating event:', error);
    console.error('Error stack:', error.stack);
    throw error;
  }
};

// Custom URL functions
const CUSTOM_URLS_COLLECTION = 'customUrls';

/**
 * Check if a custom URL extension is available (unique)
 * @param {string} urlExtension - The custom URL extension to check
 * @returns {Promise<boolean>} - Returns true if URL is available (unique)
 */
export const isCustomUrlAvailable = async (urlExtension) => {
  try {
    const customUrlsCollection = collection(db, CUSTOM_URLS_COLLECTION);
    // Normalize URL extension to lowercase for case-insensitive matching
    const normalizedUrlExtension = urlExtension.toLowerCase();
    const q = query(customUrlsCollection, where('urlExtension', '==', normalizedUrlExtension));
    const querySnapshot = await getDocs(q);
    return querySnapshot.empty; // Returns true if no documents found (URL is available)
  } catch (error) {
    throw error;
  }
};

/**
 * Create or update a custom URL mapping for an event (one per event)
 * @param {string} urlExtension - The custom URL extension
 * @param {string} eventId - The event ID this URL points to
 * @param {string} organizerId - The user ID of the organizer
 * @returns {Promise<string>} - Returns the custom URL document ID
 */
export const createCustomUrl = async (urlExtension, eventId, organizerId) => {
  try {
    const customUrlsCollection = collection(db, CUSTOM_URLS_COLLECTION);
    // Check if a custom URL already exists for this event
    const q = query(customUrlsCollection, where('eventId', '==', eventId));
    const querySnapshot = await getDocs(q);
    // Normalize URL extension to lowercase for consistent storage
    const normalizedUrlExtension = urlExtension.toLowerCase();
    const customUrlData = {
      urlExtension: normalizedUrlExtension,
      eventId,
      organizerId,
      updatedAt: serverTimestamp(),
    };
    if (!querySnapshot.empty) {
      // Overwrite the first found custom URL for this event
      const docRef = querySnapshot.docs[0].ref;
      await updateDoc(docRef, customUrlData);
      return docRef.id;
    } else {
      // Create a new custom URL
      customUrlData.createdAt = serverTimestamp();
      const docRef = await addDoc(customUrlsCollection, customUrlData);
      return docRef.id;
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Get event ID from custom URL extension (events only, not player profiles)
 * @param {string} urlExtension - The custom URL extension
 * @returns {Promise<string|null>} - Returns the event ID or null if not found
 */
export const getEventIdFromCustomUrl = async (urlExtension) => {
  try {
    const customUrlsCollection = collection(db, CUSTOM_URLS_COLLECTION);
    // Normalize URL extension to lowercase for case-insensitive matching
    const normalizedUrlExtension = urlExtension.toLowerCase();
    const q = query(customUrlsCollection, where('urlExtension', '==', normalizedUrlExtension));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // Filter for event URLs only (not player profiles)
      const eventDocs = querySnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.eventId && data.type !== 'player';
      });
      
      if (eventDocs.length > 0) {
        return eventDocs[0].data().eventId;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting event ID from custom URL:', error);
    throw error;
  }
};

/**
 * Get all custom URLs for an organizer
 * @param {string} organizerId - The user ID of the organizer
 * @returns {Promise<Array>} - Returns array of custom URL objects
 */
export const getCustomUrlsByOrganizer = async (organizerId) => {
  try {
    const customUrlsCollection = collection(db, CUSTOM_URLS_COLLECTION);
    const q = query(customUrlsCollection, where('organizerId', '==', organizerId));
    const querySnapshot = await getDocs(q);
    
    const customUrls = [];
    querySnapshot.forEach((doc) => {
      customUrls.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return customUrls;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all custom URLs for a specific event
 * @param {string} eventId - The event ID
 * @returns {Promise<Array>} - Returns array of custom URL objects for the event
 */
export const getCustomUrlsByEventId = async (eventId) => {
  try {
    const customUrlsCollection = collection(db, CUSTOM_URLS_COLLECTION);
    const q = query(customUrlsCollection, where('eventId', '==', eventId));
    const querySnapshot = await getDocs(q);
    
    const customUrls = [];
    querySnapshot.forEach((doc) => {
      customUrls.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return customUrls;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete a custom URL
 * @param {string} customUrlId - The custom URL document ID
 * @returns {Promise<void>}
 */
export const deleteCustomUrl = async (customUrlId) => {
  try {
    const customUrlRef = doc(db, CUSTOM_URLS_COLLECTION, customUrlId);
    await deleteDoc(customUrlRef);
  } catch (error) {
    throw error;
  }
};

// USERS COLLECTION
const USERS_COLLECTION = 'users';

/**
 * Create a user profile in Firestore
 * @param {string} userId - Firebase Auth UID
 * @param {Object} profileData - { email, displayName, phoneNumber, fideId, localFederationId }
 */
export const createUserProfile = async (userId, profileData) => {
  const userRef = doc(db, USERS_COLLECTION, userId);
  await setDoc(userRef, {
    userId,
    email: profileData.email,
    displayName: profileData.displayName,
    phoneNumber: profileData.phoneNumber || '',
    fideId: profileData.fideId || '',
    localFederationId: profileData.localFederationId || '',
    fideRating: profileData.fideRating || '',
    nationalRating: profileData.nationalRating || profileData.rating || '',
    // Premium/Subscription fields
    premium: true,
    subscriptionType: null, // 'monthly' | 'onetime' | null
    stripeCustomerId: null,
    subscriptionId: null,
    subscriptionStatus: null,
    subscriptionEndDate: null,
    eventLimit: 1, // Default to 1 for free users
    eventsCreated: 0,
    // Referral fields
    referrerId: profileData.referrerId || null,
    referredAt: profileData.referredAt || null,
    // Signup context fields (for email segmentation)
    signupContext: profileData.signupContext || 'creator',
    signupCoachUsername: profileData.signupCoachUsername || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

/**
 * Get a user profile by userId
 */
export const getUserProfile = async (userId) => {
  const userRef = doc(db, USERS_COLLECTION, userId);
  const snap = await getDoc(userRef);
  return snap.exists() ? snap.data() : null;
};

/**
 * Get count of events created by a user
 */
export const getUserEventCount = async (userId) => {
  const eventsQuery = query(
    collection(db, EVENTS_COLLECTION),
    where('organizerId', '==', userId)
  );
  const snapshot = await getDocs(eventsQuery);
  return snapshot.size;
};

/**
 * Update a user profile
 */
export const updateUserProfile = async (userId, profileData) => {
  const userRef = doc(db, USERS_COLLECTION, userId);
  await updateDoc(userRef, {
    ...profileData,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Admin grant premium to a user — mirrors the Stripe webhook dual-write pattern.
 * Updates both users/{uid} and all playerProfiles where userId == uid.
 */
export const adminGrantPremium = async (userId, tier = 'pro') => {
  // 1. Update the user document
  await updateUserProfile(userId, {
    playerPremium: true,
    premium: true,
    playerPremiumSource: 'admin',
    playerPremiumTier: tier,
  });

  // 2. Update all player profiles for this user
  const profiles = await getPlayerProfilesByUser(userId);
  for (const profile of profiles) {
    const profileUpdate = {
      playerPremium: true,
      playerPremiumTier: tier,
      shopOwner: true,
    };
    // Auto-upgrade layout to custom
    if (profile.theme?.layout !== 'custom') {
      profileUpdate['theme.layout'] = 'custom';
    }
    await updatePlayerProfile(profile.id, profileUpdate);
  }
};

/**
 * Admin revoke premium from a user — reverse of adminGrantPremium.
 * Sets playerPremium: false on both users/{uid} and all playerProfiles.
 */
export const adminRevokePremium = async (userId) => {
  await updateUserProfile(userId, {
    playerPremium: false,
    premium: false,
    playerPremiumSource: null,
    playerPremiumTier: null,
  });

  const profiles = await getPlayerProfilesByUser(userId);
  for (const profile of profiles) {
    await updatePlayerProfile(profile.id, {
      playerPremium: false,
      playerPremiumTier: null,
    });
  }
};

// PLAYER PROFILES COLLECTION
const PLAYER_PROFILES_COLLECTION = 'playerProfiles';

/**
 * Create a new player profile
 * @param {Object} profileData - Player profile data
 * @param {string} userId - User ID of the profile owner
 * @returns {Promise<string>} - Returns the created profile ID
 */
export const createPlayerProfile = async (profileData, userId) => {
  try {
    // Seed email from users collection if not provided
    let email = profileData.email || '';
    if (!email && userId) {
      try {
        const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId));
        if (userDoc.exists()) email = userDoc.data().email || '';
      } catch (e) { /* non-blocking */ }
    }

    const profileWithTimestamps = {
      ...profileData,
      email,
      userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'draft',
      isPublished: false,
      premium: false,
      // Allow admin support access when admin explicitly creates this item
      adminCanAccess: profileData.adminCreated === true ? true : false
    };

    const profilesCollection = collection(db, PLAYER_PROFILES_COLLECTION);
    const docRef = await addDoc(profilesCollection, profileWithTimestamps);

    return docRef.id;
  } catch (error) {
    throw error;
  }
};

/**
 * Create player profile with custom ID
 * @param {string} profileId - Custom profile ID
 * @param {Object} profileData - Player profile data
 * @param {string} userId - User ID of the profile owner
 * @returns {Promise<string>} - Returns the profile ID
 */
export const createPlayerProfileWithId = async (profileId, profileData, userId) => {
  try {
    const profileRef = doc(db, PLAYER_PROFILES_COLLECTION, profileId);
    const userRef = doc(db, USERS_COLLECTION, userId);

    const existingSnap = await getDoc(profileRef);
    const isNewProfile = !existingSnap.exists();
    const existingData = isNewProfile ? {} : existingSnap.data();

    // Seed email from users collection if not provided (new profiles only)
    let email = profileData.email || existingData.email || '';
    if (!email && userId && userId !== 'agent-system') {
      try {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) email = userDoc.data().email || '';
      } catch (e) { /* non-blocking */ }
    }

    const nextStatus = profileData.status ?? existingData.status ?? 'draft';
    const nextIsPublished = profileData.isPublished ?? existingData.isPublished ?? false;

    const profileWithTimestamps = {
      ...existingData,
      ...profileData,
      email,
      profileId,
      userId,
      status: nextStatus,
      isPublished: nextIsPublished,
      createdAt: existingData.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Preserve existing admin access or set when admin is creating
      adminCanAccess: profileData.adminCreated === true ? true : (existingData.adminCanAccess === true ? true : false)
    };

    await setDoc(profileRef, profileWithTimestamps, { merge: true });

    if (isNewProfile) {
      try {
        await updateDoc(userRef, {
          playerProfilesCreated: increment(1),
          updatedAt: serverTimestamp()
        });
      } catch (userUpdateError) {
        // Don't throw - profile creation should still succeed
      }
    }

    return profileId;
  } catch (error) {
    throw error;
  }
};

/**
 * Get a player profile by ID
 * @param {string} profileId - Profile ID
 * @returns {Promise<Object|null>} - Returns profile data or null
 */
export const getPlayerProfile = async (profileId) => {
  try {
    if (!profileId) {
      console.error('getPlayerProfile: profileId is null or undefined');
      return null;
    }
    
    const profileRef = doc(db, PLAYER_PROFILES_COLLECTION, profileId);
    const profileSnap = await getDoc(profileRef);
    
    if (profileSnap.exists()) {
      return {
        id: profileSnap.id,
        ...profileSnap.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting player profile:', error);
    throw error;
  }
};

/**
 * Get player profiles by user ID
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Returns array of profiles
 */
export const getPlayerProfilesByUser = async (userId) => {
  try {
    const profilesCollection = collection(db, PLAYER_PROFILES_COLLECTION);
    const q = query(profilesCollection, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    const profiles = [];
    querySnapshot.forEach((doc) => {
      profiles.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return profiles;
  } catch (error) {
    throw error;
  }
};

/**
 * Get count of player profiles created by a user
 * @param {string} userId - User ID
 * @returns {Promise<number>} - Returns profile count
 */
export const getUserPlayerProfileCount = async (userId) => {
  try {
    const profilesQuery = query(
      collection(db, PLAYER_PROFILES_COLLECTION),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(profilesQuery);
    return snapshot.size;
  } catch (error) {
    throw error;
  }
};

/**
 * Update a player profile
 * @param {string} profileId - Profile ID
 * @param {Object} updatedData - Updated profile data
 * @returns {Promise<void>}
 */
export const updatePlayerProfile = async (profileId, updatedData) => {
  try {
    const profileRef = doc(db, PLAYER_PROFILES_COLLECTION, profileId);
    
    const dataWithTimestamp = {
      ...updatedData,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(profileRef, dataWithTimestamp);
  } catch (error) {
    throw error;
  }
};

/**
 * Publish a player profile
 * @param {string} profileId - Profile ID
 * @returns {Promise<{isFirstPublish: boolean}>} - Returns whether this was the first publish
 */
export const publishPlayerProfile = async (profileId) => {
  try {
    const profileRef = doc(db, PLAYER_PROFILES_COLLECTION, profileId);

    // Check if this profile has been published before
    const profileSnap = await getDoc(profileRef);
    const profileData = profileSnap.data();
    const isFirstPublish = !profileData?.publishedAt;

    // console.log('🔍 publishPlayerProfile - profileData.publishedAt:', profileData?.publishedAt);
    // console.log('🔍 publishPlayerProfile - isFirstPublish:', isFirstPublish);

    // Update profile to published status
    await updateDoc(profileRef, {
      status: 'published',
      isPublished: true,
      publishedAt: profileData?.publishedAt || serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // If first publish, update user document to track this milestone
    if (isFirstPublish && profileData?.userId) {
      try {
        const userRef = doc(db, USERS_COLLECTION, profileData.userId);
        await updateDoc(userRef, {
          hasPublishedPlayerProfile: true
        });
      } catch (userError) {
        console.warn('Could not update user first publish status:', userError);
        // Don't throw - profile is still published successfully
      }
    }

    // console.log('🔍 publishPlayerProfile - returning isFirstPublish:', isFirstPublish);
    return { isFirstPublish };
  } catch (error) {
    throw error;
  }
};


/**
 * Create or update a player URL mapping using the customUrls collection
 * @param {string} username - The username/URL extension
 * @param {string} profileId - The profile ID this URL points to
 * @param {string} userId - The user ID of the owner
 * @param {boolean} isPremium - Whether this is a premium URL
 * @returns {Promise<string>} - Returns the URL document ID
 */
export const createPlayerUrl = async (username, profileId, userId, isPremium = false) => {
  try {
    const customUrlsCollection = collection(db, CUSTOM_URLS_COLLECTION);

    const normalizedUsername = username.toLowerCase();

    // Ensure the requested slug is either unused or already mapped to this profile
    const slugQuery = query(customUrlsCollection, where('urlExtension', '==', normalizedUsername));
    const slugSnapshot = await getDocs(slugQuery);
    const conflictingDoc = slugSnapshot.docs.find(doc => {
      const data = doc.data();
      return data.profileId && data.profileId !== profileId;
    });

    if (conflictingDoc) {
      throw new Error('This username is already in use by another profile.');
    }

    // Check if a custom URL already exists for this profile
    const profileQuery = query(customUrlsCollection, where('profileId', '==', profileId));
    const profileSnapshot = await getDocs(profileQuery);

    const urlData = {
      urlExtension: normalizedUsername,
      profileId,
      eventId: null,
      organizerId: userId,
      type: 'player',
      isPremium,
      updatedAt: serverTimestamp(),
    };

    // console.log('Creating player URL with data:', { ...urlData, updatedAt: 'serverTimestamp()' });

    if (!profileSnapshot.empty) {
      const docRef = profileSnapshot.docs[0].ref;
      await updateDoc(docRef, urlData);
      return docRef.id;
    }

    urlData.createdAt = serverTimestamp();
    const docRef = await addDoc(customUrlsCollection, urlData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating player URL:', error);
    throw error;
  }
};

/**
 * Get profile ID from player username using customUrls collection
 * @param {string} username - The username to look up
 * @returns {Promise<Object|null>} - Returns profile info or null
 */
export const getProfileIdFromUsername = async (username) => {
  try {
    const customUrlsCollection = collection(db, CUSTOM_URLS_COLLECTION);
    const normalizedUsername = username.toLowerCase();
    const q = query(customUrlsCollection, where('urlExtension', '==', normalizedUsername));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // Filter for player profiles client-side to avoid composite index requirement
      const playerDocs = querySnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.type === 'player' || (data.profileId && !data.eventId);
      });
      
      if (playerDocs.length > 0) {
        const doc = playerDocs[0];
        const data = doc.data();
        return {
          profileId: data.profileId,
          userId: data.organizerId,
          isPremium: data.isPremium,
          type: data.type || 'player'
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting profile from username:', error);
    throw error;
  }
};

/**
 * Get username (URL extension) from player profile ID
 * @param {string} profileId - The player profile ID
 * @returns {Promise<string|null>} - Returns username or null
 */
export const getUsernameFromProfileId = async (profileId) => {
  try {
    const customUrlsCollection = collection(db, CUSTOM_URLS_COLLECTION);
    const q = query(customUrlsCollection, where('profileId', '==', profileId));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      return data.urlExtension || null;
    }

    return null;
  } catch (error) {
    console.error('Error getting username from profileId:', error);
    throw error;
  }
};

/**
 * Get player URLs by user using customUrls collection
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Returns array of URL objects
 */
export const getPlayerUrlsByUser = async (userId) => {
  try {
    const customUrlsCollection = collection(db, CUSTOM_URLS_COLLECTION);
    const q = query(customUrlsCollection, where('organizerId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    const urls = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Filter for player profiles client-side
      if (data.type === 'player' || (data.profileId && !data.eventId)) {
        urls.push({
          id: doc.id,
          username: data.urlExtension,
          profileId: data.profileId,
          userId: data.organizerId,
          isPremium: data.isPremium,
          ...data
        });
      }
    });
    
    return urls;
  } catch (error) {
    console.error('Error getting player URLs by user:', error);
    throw error;
  }
};

/**
 * Delete a player URL from customUrls collection
 * @param {string} urlId - The URL document ID
 * @returns {Promise<void>}
 */
export const deletePlayerUrl = async (urlId) => {
  try {
    const urlRef = doc(db, CUSTOM_URLS_COLLECTION, urlId);
    await deleteDoc(urlRef);
  } catch (error) {
    console.error('Error deleting player URL:', error);
    throw error;
  }
};

/**
 * Delete a player profile
 * @param {string} profileId - Profile ID
 * @returns {Promise<void>}
 */
export const deletePlayerProfile = async (profileId) => {
  try {
    const profileRef = doc(db, PLAYER_PROFILES_COLLECTION, profileId);
    await deleteDoc(profileRef);

    // Also delete any custom URLs associated with this profile
    const customUrlsCollection = collection(db, CUSTOM_URLS_COLLECTION);
    const q = query(customUrlsCollection, where('profileId', '==', profileId));
    const querySnapshot = await getDocs(q);

    const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
  } catch (error) {
    console.error('Error deleting player profile:', error);
    throw error;
  }
};

/**
 * Transfer ownership of a player profile to a new user and reset referral data
 * @param {string} profileId - Player profile ID
 * @param {string} newOwnerUserId - New owner's user ID
 * @returns {Promise<void>}
 */
export const transferPlayerProfileOwnership = async (profileId, newOwnerUserId) => {
  try {
    if (!profileId || !newOwnerUserId) {
      throw new Error('Profile ID and new owner user ID are required');
    }

    const profileRef = doc(db, PLAYER_PROFILES_COLLECTION, profileId);

    // Verify profile exists
    const profileSnap = await getDoc(profileRef);
    if (!profileSnap.exists()) {
      throw new Error('Player profile not found');
    }

    // Update profile with new owner and reset referral fields
    const updateData = {
      userId: newOwnerUserId,
      lastModified: new Date().toISOString(),
      // Reset referral-related fields
      referralCount: 0,
      commissionEarnings: 0,
      freeMonthsEarned: 0,
      referralCountPaid: 0,
      // Remove fields that should not carry over
      referredBy: deleteField(),
      hasLifetimeSubdomain: deleteField(),
      // Clear claim token so it can't be reused
      claimToken: deleteField(),
      claimEmail: deleteField()
    };

    await updateDoc(profileRef, updateData);

    // console.log(`✅ Profile ${profileId} transferred to user ${newOwnerUserId}`);
  } catch (error) {
    console.error('Error transferring player profile ownership:', error);
    throw error;
  }
};

/**
 * Find a player profile by its claim token
 * @param {string} token - The claim token to search for
 * @returns {Promise<Object|null>} - Returns the profile data with id, or null if not found
 */
export const getProfileByClaimToken = async (token) => {
  try {
    if (!token) return null;
    const profilesRef = collection(db, PLAYER_PROFILES_COLLECTION);
    const q = query(profilesRef, where('claimToken', '==', token), limit(1));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const docSnap = snapshot.docs[0];
    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error('Error getting profile by claim token:', error);
    return null;
  }
};

/**
 * Debug function to check all custom URLs
 * @returns {Promise<Array>} - Returns all custom URLs
 */
export const debugGetAllCustomUrls = async () => {
  try {
    const customUrlsCollection = collection(db, CUSTOM_URLS_COLLECTION);
    const querySnapshot = await getDocs(customUrlsCollection);
    
    const urls = [];
    querySnapshot.forEach((doc) => {
      urls.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return urls;
  } catch (error) {
    console.error('Error getting all custom URLs:', error);
    throw error;
  }
};

// Chess Game Functions
/**
 * Get chess game data for a player profile
 * @param {string} profileId - Player profile ID
 * @returns {Promise<Object|null>} - Chess game data or null if not found
 */
export const getChessGame = async (profileId) => {
  try {
    const gameDoc = doc(db, 'playerProfiles', profileId, 'chessGames', 'current');
    const docSnap = await getDoc(gameDoc);
    
    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  } catch (error) {
    console.error('Error getting chess game:', error);
    throw error;
  }
};

/**
 * Update chess game data for a player profile
 * @param {string} profileId - Player profile ID
 * @param {Object} gameData - Chess game data to update
 * @returns {Promise<void>}
 */
export const updateChessGame = async (profileId, gameData) => {
  try {
    const gameDoc = doc(db, 'playerProfiles', profileId, 'chessGames', 'current');
    await setDoc(gameDoc, {
      ...gameData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating chess game:', error);
    throw error;
  }
};

/**
 * Reset chess game to initial position
 * @param {string} profileId - Player profile ID
 * @param {Object} options
 * @param {string} [options.ownerColor] - Owner's chosen color (white/black)
 * @param {boolean} [options.clearScore=false] - When true, wipes the running score
 * @returns {Promise<void>}
 */
export const resetChessGame = async (
  profileId,
  { ownerColor, clearScore = false } = {}
) => {
  try {
    const gameDocRef = doc(db, 'playerProfiles', profileId, 'chessGames', 'current');

    await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(gameDocRef);
      const baseData = snapshot.exists() ? snapshot.data() : {};

      const chess = new Chess();
      const baseScore = baseData.score || { authWins: 0, publicWins: 0, draws: 0 };
      const score = clearScore ? { authWins: 0, publicWins: 0, draws: 0 } : baseScore;
      const resolvedOwnerColor = ownerColor || baseData.ownerColor || 'white';

      const previousGame = snapshot.exists()
        ? {
            fen: baseData.fen || null,
            pgn: baseData.pgn || '',
            result: baseData.gameResult || null,
            completedAt: baseData.updatedAt || null,
            signature: baseData.resultSignature || null,
            score: baseData.score || { authWins: 0, publicWins: 0, draws: 0 },
            lastMove: baseData.lastMove || null,
            moves: Array.isArray(baseData.moves) ? [...baseData.moves] : []
          }
        : null;

      transaction.set(gameDocRef, {
        fen: chess.fen(),
        pgn: chess.pgn(),
        moves: [],
        lastMove: null,
        ownerColor: resolvedOwnerColor,
        score,
        gameOver: false,
        gameResult: null,
        resultSignature: null,
        previousGame,
        createdAt: baseData.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
        resetAt: serverTimestamp()
      }, { merge: true });
    });
  } catch (error) {
    console.error('Error resetting chess game:', error);
    throw error;
  }
};

/**
 * Get count of events where user is owner or co-owner
 * @param {string} userId - User ID
 * @returns {Promise<number>} - Returns total event count (owned + shared)
 */
export const getUserEventCountWithShared = async (userId) => {
  try {
    // Query for owned events
    const ownedQuery = query(
      collection(db, EVENTS_COLLECTION),
      where('organizerId', '==', userId)
    );
    const ownedSnapshot = await getDocs(ownedQuery);
    
    // Query for shared events (where user is owner2)
    const sharedQuery = query(
      collection(db, EVENTS_COLLECTION),
      where('owner2Id', '==', userId)
    );
    const sharedSnapshot = await getDocs(sharedQuery);
    
    return ownedSnapshot.size + sharedSnapshot.size;
  } catch (error) {
    console.error('Error getting user event count with shared:', error);
    throw error;
  }
};

/**
 * Get count of player profiles where user is owner or co-owner
 * @param {string} userId - User ID  
 * @returns {Promise<number>} - Returns total profile count (owned + shared)
 */
export const getUserPlayerProfileCountWithShared = async (userId) => {
  try {
    // Query for owned profiles
    const ownedQuery = query(
      collection(db, PLAYER_PROFILES_COLLECTION),
      where('userId', '==', userId)
    );
    const ownedSnapshot = await getDocs(ownedQuery);
    
    // Query for shared profiles (where user is owner2)  
    const sharedQuery = query(
      collection(db, PLAYER_PROFILES_COLLECTION),
      where('owner2Id', '==', userId)
    );
    const sharedSnapshot = await getDocs(sharedQuery);
    
    return ownedSnapshot.size + sharedSnapshot.size;
  } catch (error) {
    console.error('Error getting user profile count with shared:', error);
    throw error;
  }
};

/**
 * Get events where user is owner or co-owner
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Returns array of events with ownership info
 */
export const getUserEventsWithShared = async (userId) => {
  try {
    // Query for owned events
    const ownedQuery = query(
      collection(db, EVENTS_COLLECTION),
      where('organizerId', '==', userId)
    );
    const ownedSnapshot = await getDocs(ownedQuery);
    
    // Query for shared events
    const sharedQuery = query(
      collection(db, EVENTS_COLLECTION),
      where('owner2Id', '==', userId)
    );
    const sharedSnapshot = await getDocs(sharedQuery);

    // Query for admin-access events if caller is admin (client can filter; server rules must enforce)
    let adminSnapshot = { empty: true, forEach: () => {} };
    try {
      const adminAccessQuery = query(
        collection(db, EVENTS_COLLECTION),
        where('adminCanAccess', '==', true)
      );
      adminSnapshot = await getDocs(adminAccessQuery);
    } catch (_) {}
    
    const events = [];
    
    // Add owned events
    ownedSnapshot.forEach((doc) => {
      events.push({
        id: doc.id,
        ...doc.data(),
        ownership: 'owner'
      });
    });
    
    // Add shared events
    sharedSnapshot.forEach((doc) => {
      events.push({
        id: doc.id,
        ...doc.data(),
        ownership: 'shared'
      });
    });

    // Add admin-access events, mark as 'admin'
    adminSnapshot.forEach && adminSnapshot.forEach((doc) => {
      const data = doc.data();
      // Avoid duplicates if already owned/shared
      const alreadyIncluded = events.some((e) => e.id === doc.id);
      if (!alreadyIncluded) {
        events.push({ id: doc.id, ...data, ownership: 'admin' });
      }
    });
    
    return events;
  } catch (error) {
    console.error('Error getting user events with shared:', error);
    throw error;
  }
};

/**
 * Get player profiles where user is owner or co-owner
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Returns array of profiles with ownership info
 */
export const getUserPlayerProfilesWithShared = async (userId, isAdmin = false) => {
  try {
    // Query for owned profiles
    const ownedQuery = query(
      collection(db, PLAYER_PROFILES_COLLECTION),
      where('userId', '==', userId)
    );
    const ownedSnapshot = await getDocs(ownedQuery);

    // Query for shared profiles
    const sharedQuery = query(
      collection(db, PLAYER_PROFILES_COLLECTION),
      where('owner2Id', '==', userId)
    );
    const sharedSnapshot = await getDocs(sharedQuery);

    // Query for admin-access profiles (only if user is admin)
    let adminSnapshot = { empty: true, forEach: () => {} };
    if (isAdmin) {
      try {
        const adminAccessQuery = query(
          collection(db, PLAYER_PROFILES_COLLECTION),
          where('adminCanAccess', '==', true)
        );
        adminSnapshot = await getDocs(adminAccessQuery);
      } catch (_) {}
    }
    
    const profiles = [];
    
    // Add owned profiles
    ownedSnapshot.forEach((doc) => {
      profiles.push({
        id: doc.id,
        ...doc.data(),
        ownership: 'owner'
      });
    });
    
    // Add shared profiles
    sharedSnapshot.forEach((doc) => {
      profiles.push({
        id: doc.id,
        ...doc.data(),
        ownership: 'shared'
      });
    });

    // Add admin-access profiles, mark as 'admin'
    adminSnapshot.forEach && adminSnapshot.forEach((doc) => {
      const data = doc.data();
      const alreadyIncluded = profiles.some((p) => p.id === doc.id);
      if (!alreadyIncluded) {
        profiles.push({ id: doc.id, ...data, ownership: 'admin' });
      }
    });
    
    return profiles;
  } catch (error) {
    console.error('Error getting user profiles with shared:', error);
    throw error;
  }
};

/**
 * Check if user can edit event (owner or co-owner)
 * @param {Object} eventData - Event data object
 * @param {string} userId - User ID to check
 * @returns {boolean} - True if user can edit
 */
export const canEditEvent = (eventData, userId, isAdmin = null) => {
  return (
    eventData.organizerId === userId ||
    eventData.owner2Id === userId ||
    (eventData.adminCanAccess === true && (isAdmin === true || isAdminUserId(userId)))
  );
};

/**
 * Check if user can edit player profile (owner or co-owner)
 * @param {Object} profileData - Profile data object
 * @param {string} userId - User ID to check
 * @returns {boolean} - True if user can edit
 */
export const canEditPlayerProfile = (profileData, userId, isAdmin = null) => {
  return (
    profileData.userId === userId ||
    profileData.owner2Id === userId ||
    (profileData.adminCanAccess === true && (isAdmin === true || isAdminUserId(userId)))
  );
};

// Lightweight admin check helper. Adjust to your admin policy as needed.
// Currently checks users collection for admin === true.
const isAdminUserId = (userId) => {
  // At call-sites we only have userId; server rules should still enforce security.
  // Client-side, we rely on AuthContext userProfile.admin where available.
  // This function is a placeholder to avoid circular imports; callers should pass
  // already-known admin status when possible. For now, we simply check a cached map.
  try {
    const cached = typeof window !== 'undefined' ? window.__ADMIN_UIDS__ : undefined;
    if (cached && Array.isArray(cached)) {
      return cached.includes(userId);
    }
  } catch (_) {}
  return false;
};

/**
 * Add co-owner to event
 * @param {string} eventId - Event ID
 * @param {string} owner2Id - Co-owner user ID
 */
export const addEventCoOwner = async (eventId, owner2Id) => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    await updateDoc(eventRef, {
      owner2Id: owner2Id,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error adding event co-owner:', error);
    throw error;
  }
};

/**
 * Remove co-owner from event
 * @param {string} eventId - Event ID
 */
export const removeEventCoOwner = async (eventId) => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    await updateDoc(eventRef, {
      owner2Id: null,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error removing event co-owner:', error);
    throw error;
  }
};

/**
 * Add co-owner to player profile
 * @param {string} profileId - Profile ID
 * @param {string} owner2Id - Co-owner user ID
 */
export const addPlayerProfileCoOwner = async (profileId, owner2Id) => {
  try {
    const profileRef = doc(db, PLAYER_PROFILES_COLLECTION, profileId);
    await updateDoc(profileRef, {
      owner2Id: owner2Id,
      lastModified: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error adding profile co-owner:', error);
    throw error;
  }
};

/**
 * Remove co-owner from player profile
 * @param {string} profileId - Profile ID
 */
export const removePlayerProfileCoOwner = async (profileId) => {
  try {
    const profileRef = doc(db, PLAYER_PROFILES_COLLECTION, profileId);
    await updateDoc(profileRef, {
      owner2Id: null,
      lastModified: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error removing profile co-owner:', error);
    throw error;
  }
};

/**
 * Get user by email (for co-owner lookup)
 * Queries the users collection to find a user with the specified email
 */
export const getUserByEmail = async (email) => {
  try {
    if (!email || !email.trim()) {
      return null;
    }

    const normalizedEmail = email.trim().toLowerCase();
    
    // Query the users collection for a user with this email
    const usersQuery = query(
      collection(db, 'users'),
      where('email', '==', normalizedEmail)
    );
    
    const querySnapshot = await getDocs(usersQuery);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    // Return the first matching user (emails should be unique)
    const userDoc = querySnapshot.docs[0];
    return {
      uid: userDoc.id,
      ...userDoc.data()
    };
  } catch (error) {
    console.error('Error getting user by email:', error);
    throw error;
  }
};

// Admin Functions
/**
 * Get all invoices from eventPayments collection
 * @returns {Promise<Array>} - Array of all invoices
 */
export const getAllInvoices = async () => {
  try {
    const invoicesCollection = collection(db, 'eventPayments');
    const querySnapshot = await getDocs(invoicesCollection);

    const invoices = [];
    querySnapshot.forEach((doc) => {
      invoices.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return invoices;
  } catch (error) {
    console.error('Error getting all invoices:', error);
    throw error;
  }
};

/**
 * Get all events
 * @returns {Promise<Array>} - Array of all events
 */
export const getAllEvents = async () => {
  try {
    const eventsCollection = collection(db, EVENTS_COLLECTION);
    const querySnapshot = await getDocs(eventsCollection);

    const events = [];
    querySnapshot.forEach((doc) => {
      events.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return events;
  } catch (error) {
    console.error('Error getting all events:', error);
    throw error;
  }
};

/**
 * Get published events/tournaments for explore page
 * @param {number} limitCount - Maximum number of events to return
 * @returns {Promise<Array>} - Array of published events
 */
export const getPublishedEvents = async (limitCount = 20) => {
  try {
    const eventsCollection = collection(db, EVENTS_COLLECTION);
    // Get all events (status field may not exist on older events)
    const querySnapshot = await getDocs(eventsCollection);

    const events = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Include events that are either explicitly published or don't have a status field (legacy)
      // Exclude events with hideMe === true
      if ((!data.status || data.status === 'published') && !data.hideMe) {
        events.push({
          id: doc.id,
          ...data
        });
      }
    });

    // Sort: pinned first (in order), then by event date (upcoming first)
    events.sort((a, b) => {
      // If one is pinned and the other isn't, pinned comes first
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;

      // If both are pinned, maintain order (first pinned stays first)
      if (a.pinned && b.pinned) return 0;

      // If neither pinned, sort by event date (upcoming first, then by creation date)
      const aDate = a.eventDate?.seconds || a.createdAt?.seconds || 0;
      const bDate = b.eventDate?.seconds || b.createdAt?.seconds || 0;
      return bDate - aDate;
    });

    // Apply limit after filtering and sorting
    return events.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting published events:', error);
    throw error;
  }
};

/**
 * Get all users
 * @returns {Promise<Array>} - Array of all users
 */
export const getAllUsers = async () => {
  try {
    const usersCollection = collection(db, 'users');
    const querySnapshot = await getDocs(usersCollection);

    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return users;
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

/**
 * Get all player profiles
 * @returns {Promise<Array>} - Array of all player profiles
 */
export const getAllPlayerProfiles = async () => {
  try {
    const profilesCollection = collection(db, PLAYER_PROFILES_COLLECTION);
    const querySnapshot = await getDocs(profilesCollection);

    const profiles = [];
    querySnapshot.forEach((doc) => {
      profiles.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return profiles;
  } catch (error) {
    console.error('Error getting all player profiles:', error);
    throw error;
  }
};

/**
 * Get published player profiles for explore page
 * @param {number} limitCount - Maximum number of profiles to return
 * @returns {Promise<Array>} - Array of published player profiles
 */
export const getPublishedPlayerProfiles = async (limitCount = 20) => {
  try {
    const profilesCollection = collection(db, PLAYER_PROFILES_COLLECTION);
    // Get all profiles (status field may not exist on older profiles)
    const querySnapshot = await getDocs(profilesCollection);

    const profiles = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Include profiles that are either explicitly published or don't have a status field (legacy)
      // Exclude profiles with hideMe === true
      if ((!data.status || data.status === 'published') && !data.hideMe) {
        profiles.push({
          id: doc.id,
          ...data
        });
      }
    });

    // Sort: pinned first (in order), then by creation date (newest first)
    profiles.sort((a, b) => {
      // If one is pinned and the other isn't, pinned comes first
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;

      // If both are pinned, maintain order (first pinned stays first)
      if (a.pinned && b.pinned) return 0;

      // If neither pinned, sort by creation date
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime;
    });

    // Apply limit after filtering and sorting
    return profiles.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting published player profiles:', error);
    throw error;
  }
};

// REFERRAL FUNCTIONS

/**
 * Handle new user signup with referral code
 * @param {string} newUserId - New user's ID
 * @param {string} referrerCode - Referrer's user ID (from URL ?ref= parameter)
 * @returns {Promise<void>}
 */
export const handleReferralSignup = async (newUserId, referrerCode) => {
  try {
    if (!referrerCode || !newUserId) return;

    // Get referrer's profile
    const referrerProfilesQuery = query(
      collection(db, 'playerProfiles'),
      where('userId', '==', referrerCode),
      limit(1)
    );
    const referrerSnapshot = await getDocs(referrerProfilesQuery);

    if (referrerSnapshot.empty) {
      // console.log('Referrer profile not found');
      return;
    }

    const referrerProfile = referrerSnapshot.docs[0];
    const referrerProfileId = referrerProfile.id;
    const currentReferralCount = referrerProfile.data().referralCount || 0;

    // Mark new user as referred by this referrer (will be applied when they create their profile)
    // Note: This creates a pending referral that will be completed when user publishes their profile

    // Increment referrer's referral count immediately (can be decremented later if account is fake)
    await updateDoc(doc(db, 'playerProfiles', referrerProfileId), {
      referralCount: increment(1)
    });

    // Check if referrer hit milestone (every 3 referrals)
    const newReferralCount = currentReferralCount + 1;
    if (newReferralCount % 3 === 0) {
      await grantReferralRewards(referrerCode, referrerProfileId);
    }

    // console.log(`✅ Referral tracked: ${newUserId} referred by ${referrerCode}`);
  } catch (error) {
    console.error('Error handling referral signup:', error);
    // Don't throw - referral tracking failure shouldn't block signup
  }
};

/**
 * Grant rewards at referral milestone (every 3 referrals)
 * @param {string} userId - User ID
 * @param {string} profileId - Profile ID
 * @returns {Promise<void>}
 */
export const grantReferralRewards = async (userId, profileId) => {
  try {
    const profileRef = doc(db, 'playerProfiles', profileId);
    const profileSnap = await getDoc(profileRef);

    if (!profileSnap.exists()) {
      console.error('Profile not found for referral rewards');
      return;
    }

    const profile = profileSnap.data();

    // Calculate new premium expiration date
    const currentExpiry = profile.premiumExpiresAt?.toDate() || new Date();
    const effectiveExpiry = currentExpiry > new Date() ? currentExpiry : new Date();
    const newExpiry = new Date(effectiveExpiry);
    newExpiry.setDate(newExpiry.getDate() + 30); // Add 30 days

    const updates = {
      freeMonthsEarned: increment(1),
      playerPremium: true, // Activate premium
      premiumExpiresAt: newExpiry
    };

    // First-time milestone: Grant LIFETIME subdomain
    if (!profile.hasLifetimeSubdomain && (profile.referralCount || 0) >= 3) {
      updates.hasLifetimeSubdomain = true;
    }

    await updateDoc(profileRef, updates);

    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    const userData = userSnap.exists() ? userSnap.data() : {};

    const userCurrentExpiry = userData?.premiumExpiresAt instanceof Timestamp
      ? userData.premiumExpiresAt.toDate()
      : userData?.premiumExpiresAt?.toDate
        ? userData.premiumExpiresAt.toDate()
        : userData?.premiumExpiresAt
          ? new Date(userData.premiumExpiresAt)
          : null;

    const finalUserExpiry = userCurrentExpiry && userCurrentExpiry > newExpiry
      ? userCurrentExpiry
      : newExpiry;

    const userUpdates = {
      playerPremium: true,
      premiumExpiresAt: Timestamp.fromDate(finalUserExpiry),
      updatedAt: serverTimestamp()
    };

    if (!userData?.premiumActivatedAt) {
      userUpdates.premiumActivatedAt = serverTimestamp();
    }

    if (!userData?.playerPremiumSource) {
      userUpdates.playerPremiumSource = 'referral';
    }

    await updateDoc(userRef, userUpdates);

    // console.log(`🎉 Referral milestone reached! Granted 30 days premium to user ${userId}`);
  } catch (error) {
    console.error('Error granting referral rewards:', error);
    throw error;
  }
};

/**
 * Handle when a referred user converts to premium (pays for subscription)
 * @param {string} userId - User ID who just paid
 * @param {number} subscriptionAmount - Amount paid
 * @returns {Promise<void>}
 */
export const handleReferralConversion = async (userId, subscriptionAmount) => {
  try {
    // Find user's profile to get their referrer
    const userProfilesQuery = query(
      collection(db, 'playerProfiles'),
      where('userId', '==', userId),
      limit(1)
    );
    const userSnapshot = await getDocs(userProfilesQuery);

    if (userSnapshot.empty) {
      // console.log('User profile not found for referral conversion');
      return;
    }

    const userProfile = userSnapshot.docs[0].data();
    const referrerId = userProfile.referredBy;

    if (!referrerId) {
      // console.log('User was not referred by anyone');
      return;
    }

    // Get referrer's profile
    const referrerProfilesQuery = query(
      collection(db, 'playerProfiles'),
      where('userId', '==', referrerId),
      limit(1)
    );
    const referrerSnapshot = await getDocs(referrerProfilesQuery);

    if (referrerSnapshot.empty) {
      // console.log('Referrer profile not found');
      return;
    }

    const referrerProfile = referrerSnapshot.docs[0];
    const referrerData = referrerProfile.data();

    // Only award commission if referrer is premium
    if (referrerData.playerPremium) {
      const commission = subscriptionAmount * 0.20; // 20% commission

      await updateDoc(doc(db, 'playerProfiles', referrerProfile.id), {
        commissionEarnings: increment(commission),
        referralCountPaid: increment(1)
      });

      // console.log(`💰 Commission earned: $${commission.toFixed(2)} for referrer ${referrerId}`);
    }
  } catch (error) {
    console.error('Error handling referral conversion:', error);
    // Don't throw - commission tracking failure shouldn't block payment
  }
};

/**
 * Mark new user as referred (called during profile creation)
 * @param {string} profileId - New user's profile ID
 * @param {string} referrerCode - Referrer's user ID
 * @returns {Promise<void>}
 */
export const markProfileAsReferred = async (profileId, referrerCode) => {
  try {
    if (!referrerCode) return;

    await updateDoc(doc(db, 'playerProfiles', profileId), {
      referredBy: referrerCode
    });

    // console.log(`✅ Profile ${profileId} marked as referred by ${referrerCode}`);
  } catch (error) {
    console.error('Error marking profile as referred:', error);
    // Don't throw - this shouldn't block profile creation
  }
};

/**
 * Get referral stats for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Returns referral stats
 */
export const getReferralStats = async (userId) => {
  try {
    const statsRef = doc(db, 'referralStats', userId);
    const snap = await getDoc(statsRef);
    return snap.exists() ? snap.data() : {
      signups: 0,
      published: 0,
      paidCount: 0,
      paidGross: 0,
      lastUpdated: null
    };
  } catch (error) {
    console.error('Error getting referral stats:', error);
    throw error;
  }
};

/**
 * Get referral events with optional filtering
 * @param {string} userId - User ID
 * @param {Object} options - Optional filters (type, limit)
 * @returns {Promise<Array>} - Returns array of referral events
 */
export const getReferralEvents = async (userId, options = {}) => {
  try {
    const eventsRef = collection(db, 'referralEvents');
    let q = query(eventsRef, where('referrerId', '==', userId));

    if (options.type) {
      q = query(q, where('type', '==', options.type));
    }

    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    const snapshot = await getDocs(q);
    const events = [];
    snapshot.forEach((doc) => {
      events.push({ id: doc.id, ...doc.data() });
    });

    return events;
  } catch (error) {
    console.error('Error getting referral events:', error);
    throw error;
  }
};

/**
 * Get all referral stats (admin only)
 * @returns {Promise<Array>} - Returns array of all referral stats
 */
export const getAllReferralStats = async () => {
  try {
    const statsRef = collection(db, 'referralStats');
    const snapshot = await getDocs(statsRef);

    const stats = [];
    snapshot.forEach((doc) => {
      stats.push({
        userId: doc.id,
        ...doc.data()
      });
    });

    return stats;
  } catch (error) {
    console.error('Error getting all referral stats:', error);
    throw error;
  }
};

/**
 * Get all referral events (admin only)
 * @param {Object} options - Optional filters (type, limit)
 * @returns {Promise<Array>} - Returns array of all referral events
 */
export const getAllReferralEvents = async (options = {}) => {
  try {
    const eventsRef = collection(db, 'referralEvents');
    let q = eventsRef;

    if (options.type) {
      q = query(eventsRef, where('type', '==', options.type));
    }

    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    const snapshot = await getDocs(q);
    const events = [];
    snapshot.forEach((doc) => {
      events.push({ id: doc.id, ...doc.data() });
    });

    return events;
  } catch (error) {
    console.error('Error getting all referral events:', error);
    throw error;
  }
};

// ==================== Blog Functions ====================

/**
 * Helper: Generate URL-friendly slug from title
 * @param {string} title - Blog title
 * @returns {string} - URL-friendly slug
 */
export const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Helper: Generate excerpt from content (strip markdown, take first 150 chars)
 * @param {string} content - Blog content
 * @param {number} maxLength - Maximum length (default 150)
 * @returns {string} - Excerpt
 */
export const generateExcerpt = (content, maxLength = 150) => {
  let stripped = content
    // First, remove ALL code blocks (including chess blocks)
    .replace(/```[\w]*\n?[\s\S]*?```/g, '')
    // Remove headings (# ## ###)
    .replace(/^#{1,3}\s+/gm, '')
    // Remove markdown-style links [text](url) - keep just the text
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    // Remove bold
    .replace(/\*\*(.*?)\*\*/g, '$1')
    // Remove italic
    .replace(/\*(.*?)\*/g, '$1')
    // Remove underline
    .replace(/__(.*?)__/g, '$1')
    // Remove old-style links
    .replace(/\(\(\((.*?)\)\)\)/g, '$1')
    // Clean up extra whitespace
    .replace(/\s+/g, ' ')
    .trim();

  return stripped.length > maxLength
    ? stripped.substring(0, maxLength) + '...'
    : stripped;
};

/**
 * Create a new blog
 * @param {Object} blogData - Blog data object
 * @returns {Promise<string>} - Returns the created blog ID
 */
export const createBlog = async (blogData) => {
  try {
    const blogsRef = collection(db, 'blogs');
    const docRef = await addDoc(blogsRef, {
      ...blogData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating blog:', error);
    throw error;
  }
};

/**
 * Get all blogs for a profile (includes drafts - for editor)
 * @param {string} profileId - Profile ID
 * @returns {Promise<Array>} - Returns array of blogs
 */
export const getBlogsByProfileId = async (profileId) => {
  try {
    const blogsRef = collection(db, 'blogs');
    const q = query(
      blogsRef,
      where('profileId', '==', profileId)
    );
    const snapshot = await getDocs(q);
    const blogs = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Exclude blogs with hideMe === true
      if (!data.hideMe) {
        blogs.push({ id: doc.id, ...data });
      }
    });
    // Sort by createdAt descending (newest first) on client side
    blogs.sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime;
    });
    return blogs;
  } catch (error) {
    console.error('Error getting blogs by profile:', error);
    throw error;
  }
};

/**
 * Get published blogs for a profile (public view)
 * @param {string} profileId - Profile ID
 * @returns {Promise<Array>} - Returns array of published blogs
 */
export const getPublishedBlogsByProfileId = async (profileId) => {
  try {
    const blogsRef = collection(db, 'blogs');
    const q = query(
      blogsRef,
      where('profileId', '==', profileId),
      where('status', '==', 'published')
    );
    const snapshot = await getDocs(q);
    const blogs = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Exclude blogs with hideMe === true
      if (!data.hideMe) {
        blogs.push({ id: doc.id, ...data });
      }
    });
    // Sort by createdAt descending (newest first)
    blogs.sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime;
    });
    return blogs;
  } catch (error) {
    console.error('Error getting published blogs:', error);
    throw error;
  }
};

/**
 * Get all published blogs (for Explore page)
 * @param {number} limitCount - Number of blogs to fetch (default 20)
 * @returns {Promise<Array>} - Returns array of published blogs
 */
export const getAllPublishedBlogs = async (limitCount = 20) => {
  try {
    const blogsRef = collection(db, 'blogs');
    const q = query(
      blogsRef,
      where('status', '==', 'published')
    );
    const snapshot = await getDocs(q);
    const blogs = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Exclude blogs with hideMe === true
      if (!data.hideMe) {
        blogs.push({ id: doc.id, ...data });
      }
    });
    // Sort: pinned first (in order), then by createdAt descending (newest first)
    blogs.sort((a, b) => {
      // If one is pinned and the other isn't, pinned comes first
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;

      // If both are pinned, maintain order (first pinned stays first)
      if (a.pinned && b.pinned) return 0;

      // If neither pinned, sort by creation date
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime;
    });
    // Apply limit on client side
    return blogs.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting all published blogs:', error);
    throw error;
  }
};

/**
 * Get single blog by ID
 * @param {string} blogId - Blog ID
 * @returns {Promise<Object|null>} - Returns blog data or null if not found/hidden
 */
export const getBlogById = async (blogId) => {
  try {
    const blogRef = doc(db, 'blogs', blogId);
    const blogSnap = await getDoc(blogRef);
    if (!blogSnap.exists()) return null;

    const data = blogSnap.data();
    // Return null if blog is hidden
    if (data.hideMe) return null;

    return { id: blogSnap.id, ...data };
  } catch (error) {
    console.error('Error getting blog by ID:', error);
    throw error;
  }
};

/**
 * Get single blog by username and slug (SEO-friendly URLs)
 * @param {string} username - Author's username
 * @param {string} slug - Blog slug
 * @returns {Promise<Object|null>} - Returns blog data or null if not found/hidden
 */
export const getBlogByUsernameAndSlug = async (username, slug) => {
  try {
    const blogsRef = collection(db, 'blogs');
    const q = query(
      blogsRef,
      where('authorUsername', '==', username),
      where('slug', '==', slug),
      where('status', '==', 'published')
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) return null;

    const docSnap = snapshot.docs[0];
    const data = docSnap.data();

    // Return null if blog is hidden
    if (data.hideMe) return null;

    return { id: docSnap.id, ...data };
  } catch (error) {
    console.error('Error getting blog by username and slug:', error);
    throw error;
  }
};

/**
 * Update blog
 * @param {string} blogId - Blog ID
 * @param {Object} updates - Updates to apply
 * @returns {Promise<void>}
 */
export const updateBlog = async (blogId, updates) => {
  try {
    const blogRef = doc(db, 'blogs', blogId);
    await updateDoc(blogRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating blog:', error);
    throw error;
  }
};

/**
 * Delete blog (soft delete - sets hideMe flag and removes ownership)
 * @param {string} blogId - Blog ID
 * @returns {Promise<void>}
 */
export const deleteBlog = async (blogId) => {
  try {
    const blogRef = doc(db, 'blogs', blogId);
    await updateDoc(blogRef, {
      hideMe: true,
      userId: null,
      profileId: null,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error deleting blog:', error);
    throw error;
  }
};

/**
 * Pin a blog (unpins any other pinned blog for this profile)
 * @param {string} blogId - Blog ID to pin
 * @param {string} profileId - Profile ID
 * @returns {Promise<void>}
 */
export const pinBlog = async (blogId, profileId) => {
  try {
    // First, unpin any currently pinned blog for this profile
    const blogsRef = collection(db, 'blogs');
    const q = query(
      blogsRef,
      where('profileId', '==', profileId),
      where('pinned', '==', true)
    );

    const querySnapshot = await getDocs(q);

    // Unpin all previously pinned blogs
    const unpinPromises = querySnapshot.docs.map(doc =>
      updateDoc(doc.ref, { pinned: false, pinnedAt: null })
    );
    await Promise.all(unpinPromises);

    // Pin the target blog
    const blogRef = doc(db, 'blogs', blogId);
    await updateDoc(blogRef, {
      pinned: true,
      pinnedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error pinning blog:', error);
    throw error;
  }
};

/**
 * Unpin a blog
 * @param {string} blogId - Blog ID to unpin
 * @returns {Promise<void>}
 */
export const unpinBlog = async (blogId) => {
  try {
    const blogRef = doc(db, 'blogs', blogId);
    await updateDoc(blogRef, {
      pinned: false,
      pinnedAt: null
    });
  } catch (error) {
    console.error('Error unpinning blog:', error);
    throw error;
  }
};

// ============================================================================
// PRODUCTS FUNCTIONS
// ============================================================================

/**
 * Get all active products from all vendors
 * @param {number} limitCount - Maximum number of products to return
 * @returns {Promise<Array>} - Returns array of products with vendor info
 */
export const getAllActiveProducts = async (limitCount = 20) => {
  try {
    const productsRef = collection(db, 'products');
    const q = query(
      productsRef,
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const products = [];

    // Collect vendor IDs to fetch vendor data
    const vendorIds = new Set();
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.vendorId) {
        vendorIds.add(data.vendorId);
      }
    });

    // Fetch vendor profile data
    const vendorProfiles = {};
    for (const vendorId of vendorIds) {
      try {
        const profileQuery = query(
          collection(db, 'playerProfiles'),
          where('userId', '==', vendorId),
          limit(1)
        );
        const profileSnapshot = await getDocs(profileQuery);
        if (!profileSnapshot.empty) {
          const profileDoc = profileSnapshot.docs[0];
          const profileData = profileDoc.data();
          vendorProfiles[vendorId] = {
            profileId: profileDoc.id,
            username: profileData.username,
            displayName: profileData.displayName,
            photoUrl: profileData.photoUrl,
            hideMe: profileData.hideMe
          };
        }
      } catch (error) {
        console.error(`Error fetching vendor profile for ${vendorId}:`, error);
      }
    }

    // Build products array with vendor info
    snapshot.forEach((doc) => {
      const data = doc.data();
      const vendorInfo = vendorProfiles[data.vendorId];

      // Exclude products from hidden vendors
      if (vendorInfo && !vendorInfo.hideMe) {
        products.push({
          id: doc.id,
          ...data,
          vendorProfileId: vendorInfo.profileId,
          vendorUsername: vendorInfo.username,
          vendorDisplayName: vendorInfo.displayName,
          vendorPhotoUrl: vendorInfo.photoUrl
        });
      }
    });

    // Apply limit
    return products.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting all active products:', error);
    throw error;
  }
};

// ============================================================================
// DIRECT MESSAGES FUNCTIONS
// ============================================================================

/**
 * Submit a direct message
 * @param {string} profileId - Profile ID to send message to
 * @param {string} senderEmail - Sender's email
 * @param {string} senderName - Sender's name (optional)
 * @param {string} message - Message content
 * @param {boolean} autoSubscribe - Whether to auto-subscribe sender
 * @returns {Promise<string>} - Returns the message ID
 */
export const submitDirectMessage = async (profileId, senderEmail, senderName, message, autoSubscribe) => {
  try {
    const messageData = {
      profileId,
      senderEmail,
      senderName: senderName || '',
      message,
      status: 'unread',
      autoSubscribed: autoSubscribe,
      createdAt: serverTimestamp()
    };

    const messagesCollection = collection(db, 'directMessages');
    const docRef = await addDoc(messagesCollection, messageData);

    // If auto-subscribe is true, add to email subscribers
    if (autoSubscribe) {
      await addEmailSubscriber(profileId, senderEmail, senderName, 'dm');
    }

    return docRef.id;
  } catch (error) {
    console.error('Error submitting direct message:', error);
    throw error;
  }
};

/**
 * Get direct messages for a profile
 * @param {string} profileId - Profile ID
 * @returns {Promise<Array>} - Returns array of messages
 */
export const getDirectMessages = async (profileId) => {
  try {
    const messagesQuery = query(
      collection(db, 'directMessages'),
      where('profileId', '==', profileId)
    );

    const snapshot = await getDocs(messagesQuery);
    const messages = [];

    snapshot.forEach(doc => {
      messages.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Sort by createdAt descending (newest first)
    messages.sort((a, b) => {
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    return messages;
  } catch (error) {
    console.error('Error getting direct messages:', error);
    throw error;
  }
};

/**
 * Mark a message as read
 * @param {string} messageId - Message ID
 * @returns {Promise<void>}
 */
export const markMessageAsRead = async (messageId) => {
  try {
    const messageRef = doc(db, 'directMessages', messageId);
    await updateDoc(messageRef, {
      status: 'read'
    });
  } catch (error) {
    console.error('Error marking message as read:', error);
    throw error;
  }
};

/**
 * Check DM rate limit for a visitor (using transaction for atomicity)
 * @param {string} profileId - Profile ID
 * @param {string} visitorId - Visitor identifier (IP or fingerprint)
 * @returns {Promise<boolean>} - Returns true if allowed, false if rate limited
 */
export const checkDMRateLimit = async (profileId, visitorId) => {
  try {
    const rateLimitId = `${profileId}_${visitorId}`;
    const rateLimitRef = doc(db, 'dmRateLimits', rateLimitId);

    const result = await runTransaction(db, async (transaction) => {
      const rateLimitDoc = await transaction.get(rateLimitRef);

      const now = Date.now();
      const oneHourAgo = now - (60 * 60 * 1000); // 1 hour in milliseconds

      if (!rateLimitDoc.exists()) {
        // First message, create rate limit doc
        transaction.set(rateLimitRef, {
          profileId,
          visitorIdentifier: visitorId,
          count: 1,
          windowStart: serverTimestamp(),
          lastMessageAt: serverTimestamp()
        });
        return true;
      }

      const data = rateLimitDoc.data();
      const windowStart = data.windowStart?.toMillis() || 0;

      // Check if window has expired (older than 1 hour)
      if (windowStart < oneHourAgo) {
        // Reset window
        transaction.update(rateLimitRef, {
          count: 1,
          windowStart: serverTimestamp(),
          lastMessageAt: serverTimestamp()
        });
        return true;
      }

      // Within window, check count
      if (data.count >= 3) {
        return false; // Rate limited
      }

      // Increment count
      transaction.update(rateLimitRef, {
        count: increment(1),
        lastMessageAt: serverTimestamp()
      });
      return true;
    });

    return result;
  } catch (error) {
    console.error('Error checking DM rate limit:', error);
    // On error, allow the message (fail open)
    return true;
  }
};

// ============================================================================
// EMAIL SUBSCRIBERS FUNCTIONS
// ============================================================================

/**
 * Add an email subscriber
 * @param {string} profileId - Profile ID
 * @param {string} email - Subscriber email
 * @param {string} subscriberName - Subscriber name
 * @param {string} source - Source of subscription (blog-post, blog-preview, dm)
 * @returns {Promise<string>} - Returns the subscriber ID
 */
export const addEmailSubscriber = async (profileId, email, subscriberName, source) => {
  try {
    // Check if already subscribed
    const existingQuery = query(
      collection(db, 'emailSubscribers'),
      where('profileId', '==', profileId),
      where('email', '==', email.toLowerCase())
    );

    const existingSnapshot = await getDocs(existingQuery);

    if (!existingSnapshot.empty) {
      // Already subscribed
      const existingDoc = existingSnapshot.docs[0];
      const existingData = existingDoc.data();

      // If previously unsubscribed, resubscribe
      if (existingData.unsubscribed) {
        await updateDoc(doc(db, 'emailSubscribers', existingDoc.id), {
          unsubscribed: false,
          unsubscribedAt: null,
          resubscribedAt: serverTimestamp(),
          subscriberName: subscriberName || existingData.subscriberName || '', // Update name
          source // Update source
        });
        return existingDoc.id;
      }

      // Already subscribed and active
      return existingDoc.id;
    }

    // Create new subscriber
    const subscriberData = {
      profileId,
      email: email.toLowerCase(),
      subscriberName: subscriberName || '',
      source,
      subscribedAt: serverTimestamp(),
      unsubscribed: false,
      unsubscribedAt: null
    };

    const subscribersCollection = collection(db, 'emailSubscribers');
    const docRef = await addDoc(subscribersCollection, subscriberData);

    return docRef.id;
  } catch (error) {
    console.error('Error adding email subscriber:', error);
    throw error;
  }
};

/**
 * Get email subscribers for a profile
 * @param {string} profileId - Profile ID
 * @returns {Promise<Array>} - Returns array of subscribers
 */
export const getEmailSubscribers = async (profileId) => {
  try {
    const subscribersQuery = query(
      collection(db, 'emailSubscribers'),
      where('profileId', '==', profileId),
      where('unsubscribed', '==', false)
    );

    const snapshot = await getDocs(subscribersQuery);
    const subscribers = [];

    snapshot.forEach(doc => {
      subscribers.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Sort by subscribedAt descending (newest first)
    subscribers.sort((a, b) => {
      const aTime = a.subscribedAt?.toMillis() || 0;
      const bTime = b.subscribedAt?.toMillis() || 0;
      return bTime - aTime;
    });

    return subscribers;
  } catch (error) {
    console.error('Error getting email subscribers:', error);
    throw error;
  }
};

/**
 * Unsubscribe an email
 * @param {string} subscriberId - Subscriber document ID
 * @returns {Promise<void>}
 */
export const unsubscribeEmail = async (subscriberId) => {
  try {
    const subscriberRef = doc(db, 'emailSubscribers', subscriberId);
    await updateDoc(subscriberRef, {
      unsubscribed: true,
      unsubscribedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error unsubscribing email:', error);
    throw error;
  }
};

/**
 * Set platform-level email unsubscribe on a user document (found by email).
 * Sets emailUnsubscribed: true and emailUnsubscribedAt on the user doc.
 * @param {string} email - Email address to unsubscribe
 * @returns {Promise<boolean>} - true if user found and updated, false if not found
 */
export const setEmailUnsubscribed = async (email) => {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      where('email', '==', email.toLowerCase()),
      limit(1)
    );
    const snapshot = await getDocs(usersQuery);
    if (snapshot.empty) return false;

    const userDoc = snapshot.docs[0];
    await updateDoc(doc(db, 'users', userDoc.id), {
      emailUnsubscribed: true,
      emailUnsubscribedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error setting email unsubscribed:', error);
    throw error;
  }
};

/**
 * Delete an email subscriber by clearing ownership fields
 * @param {string} subscriberId - Subscriber document ID
 * @returns {Promise<void>}
 */
export const deleteEmailSubscriber = async (subscriberId) => {
  try {
    const subscriberRef = doc(db, 'emailSubscribers', subscriberId);
    await updateDoc(subscriberRef, {
      profileId: null,
      email: null,
      subscriberName: null,
      unsubscribed: true,
      deletedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error deleting email subscriber:', error);
    throw error;
  }
};

/**
 * Get subscriber by profile and email (for unsubscribe lookup)
 * @param {string} profileId - Profile ID
 * @param {string} email - Email address
 * @returns {Promise<Object|null>} - Returns subscriber doc or null
 */
export const getSubscriberByEmail = async (profileId, email) => {
  try {
    const subscriberQuery = query(
      collection(db, 'emailSubscribers'),
      where('profileId', '==', profileId),
      where('email', '==', email.toLowerCase()),
      limit(1)
    );

    const snapshot = await getDocs(subscriberQuery);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('Error getting subscriber by email:', error);
    throw error;
  }
};

// ============================================================================
// PAYMENT EVENT LOGS FUNCTIONS
// ============================================================================

// Collection reference
const PAYMENT_EVENT_LOGS_COLLECTION = 'paymentEventLogs';

/**
 * Create payment event log
 * @param {Object} logData - Payment event data
 * @returns {Promise<string>} - Returns log document ID
 */
export const createPaymentEventLog = async (logData) => {
  try {
    const timestamp = Date.now();
    const documentId = `${logData.eventId}_${logData.participantId}_${timestamp}`;

    const paymentLog = {
      ...logData,
      timestamp: serverTimestamp(),
      immutable: true,
      createdAt: serverTimestamp()
    };

    const logRef = doc(db, PAYMENT_EVENT_LOGS_COLLECTION, documentId);
    await setDoc(logRef, paymentLog);

    return documentId;
  } catch (error) {
    console.error('Error creating payment event log:', error);
    throw error;
  }
};

/**
 * Get payment event logs for an event
 * @param {string} eventId - Event ID
 * @returns {Promise<Array>} - Returns array of payment logs
 */
export const getPaymentEventLogs = async (eventId) => {
  try {
    const logsCollection = collection(db, PAYMENT_EVENT_LOGS_COLLECTION);
    const logsQuery = query(
      logsCollection,
      where('eventId', '==', eventId)
    );

    const snapshot = await getDocs(logsQuery);
    const logs = [];
    snapshot.forEach((doc) => {
      logs.push({ id: doc.id, ...doc.data() });
    });

    // Sort by timestamp descending (newest first) on client side
    logs.sort((a, b) => {
      const aTime = a.timestamp?.seconds || 0;
      const bTime = b.timestamp?.seconds || 0;
      return bTime - aTime;
    });

    return logs;
  } catch (error) {
    console.error('Error getting payment event logs:', error);
    throw error;
  }
};

/**
 * Search all payment event logs (admin only)
 * @param {Object} filters - Search filters
 * @returns {Promise<Array>} - Returns filtered logs
 */
export const searchPaymentEventLogs = async (filters = {}) => {
  try {
    const logsCollection = collection(db, PAYMENT_EVENT_LOGS_COLLECTION);
    let logsQuery = logsCollection;

    // Build query with filters
    const constraints = [];

    if (filters.eventId) {
      constraints.push(where('eventId', '==', filters.eventId));
    }
    if (filters.participantId) {
      constraints.push(where('participantId', '==', filters.participantId));
    }
    if (filters.eventType) {
      constraints.push(where('eventType', '==', filters.eventType));
    }
    if (filters.changedBy) {
      constraints.push(where('changedBy', '==', filters.changedBy));
    }

    // Add date range filtering
    if (filters.startDate) {
      try {
        const startDate = new Date(filters.startDate);
        startDate.setHours(0, 0, 0, 0); // Start of day
        constraints.push(where('timestamp', '>=', Timestamp.fromDate(startDate)));
      } catch (error) {
        console.error('Invalid start date:', filters.startDate);
      }
    }
    if (filters.endDate) {
      try {
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999); // End of day
        constraints.push(where('timestamp', '<=', Timestamp.fromDate(endDate)));
      } catch (error) {
        console.error('Invalid end date:', filters.endDate);
      }
    }

    // Add limit
    const limitCount = filters.limit || 100;

    if (constraints.length > 0) {
      logsQuery = query(logsCollection, ...constraints, limit(limitCount));
    } else {
      logsQuery = query(logsCollection, limit(limitCount));
    }

    const snapshot = await getDocs(logsQuery);
    const logs = [];
    snapshot.forEach((doc) => {
      logs.push({ id: doc.id, ...doc.data() });
    });

    // Sort by timestamp descending (newest first)
    logs.sort((a, b) => {
      const aTime = a.timestamp?.seconds || 0;
      const bTime = b.timestamp?.seconds || 0;
      return bTime - aTime;
    });

    return logs;
  } catch (error) {
    console.error('Error searching payment event logs:', error);
    throw error;
  }
};

// ============================================================================
// LICHESS INTEGRATION FUNCTIONS
// ============================================================================

/**
 * Save Lichess OAuth token and username for a user
 * @param {string} userId - User ID
 * @param {string} accessToken - Lichess OAuth access token
 * @param {string} username - Lichess username
 * @returns {Promise<void>}
 */
export const saveLichessToken = async (userId, accessToken, username) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      'lichess.accessToken': accessToken,
      'lichess.username': username,
      'lichess.connectedAt': serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error saving Lichess token:', error);
    throw error;
  }
};

/**
 * Get Lichess OAuth data for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} - Returns Lichess data or null
 */
export const getLichessToken = async (userId) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const userData = userSnap.data();
      return userData.lichess || null;
    }
    return null;
  } catch (error) {
    console.error('Error getting Lichess token:', error);
    throw error;
  }
};

/**
 * Remove Lichess connection for a user
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const removeLichessConnection = async (userId) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      lichess: deleteField(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error removing Lichess connection:', error);
    throw error;
  }
};

// ==================== Coach Functions ====================
const COACHES_COLLECTION = 'coaches';

/**
 * Create a coach profile linked to a player profile
 * @param {string} playerProfileId - The player profile ID to link to
 * @param {string} userId - User ID of the coach
 * @param {Object} initialData - Initial coach data (displayName, etc.)
 * @returns {Promise<string>} - Returns the created coach ID
 */
export const createCoachProfile = async (playerProfileId, userId, initialData = {}) => {
  try {
    const coachRef = doc(collection(db, COACHES_COLLECTION));
    const coachData = {
      playerProfileId,
      userId,
      displayName: initialData.displayName || '',
      bio: initialData.bio || '',
      packagePricing: initialData.packagePricing || {
        sessions1: { usd: 15, perk: '' },
        sessions4: { usd: 52, perk: '' },
        sessions7: { usd: 84, perk: '' },
      },
      status: initialData.status || 'onboarding',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(coachRef, coachData);

    // Link back to playerProfile
    const playerProfileRef = doc(db, PLAYER_PROFILES_COLLECTION, playerProfileId);
    await updateDoc(playerProfileRef, {
      coachId: coachRef.id,
      updatedAt: serverTimestamp(),
    });

    return coachRef.id;
  } catch (error) {
    console.error('Error creating coach profile:', error);
    throw error;
  }
};

/**
 * Get a coach profile by ID
 * @param {string} coachId - Coach ID
 * @returns {Promise<Object|null>} - Returns coach data or null
 */
export const getCoach = async (coachId) => {
  try {
    const coachRef = doc(db, COACHES_COLLECTION, coachId);
    const coachSnap = await getDoc(coachRef);

    if (coachSnap.exists()) {
      return {
        id: coachSnap.id,
        ...coachSnap.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting coach:', error);
    throw error;
  }
};

/**
 * Get coach profile by player profile ID
 * @param {string} playerProfileId - Player profile ID
 * @returns {Promise<Object|null>} - Returns coach data or null
 */
export const getCoachByPlayerProfile = async (playerProfileId) => {
  try {
    const coachesRef = collection(db, COACHES_COLLECTION);
    const q = query(coachesRef, where('playerProfileId', '==', playerProfileId), limit(1));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting coach by player profile:', error);
    throw error;
  }
};

/**
 * Update a coach profile
 * @param {string} coachId - Coach ID
 * @param {Object} data - Data to update
 * @returns {Promise<void>}
 */
export const updateCoach = async (coachId, data) => {
  try {
    const coachRef = doc(db, COACHES_COLLECTION, coachId);
    await updateDoc(coachRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating coach:', error);
    throw error;
  }
};

/**
 * Get all coaches (admin function)
 * @returns {Promise<Array>} - Returns array of all coaches
 */
export const getAllCoaches = async () => {
  try {
    const coachesRef = collection(db, COACHES_COLLECTION);
    const querySnapshot = await getDocs(coachesRef);

    const coaches = [];
    querySnapshot.forEach((doc) => {
      coaches.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return coaches;
  } catch (error) {
    console.error('Error getting all coaches:', error);
    throw error;
  }
};

/**
 * Get coach profile by username (for URL-based routing)
 * Lookup chain: username → customUrls → profileId → playerProfile → coachId → coach
 * @param {string} username - The username from the URL
 * @returns {Promise<Object|null>} - Returns { coach, playerProfile, profileId, username } or null
 */
export const getCoachByUsername = async (username) => {
  try {
    // Step 1: Get profileId from username via customUrls
    const userInfo = await getProfileIdFromUsername(username);
    if (!userInfo?.profileId) {
      // console.log('getCoachByUsername: No profile found for username:', username);
      return null;
    }

    // Step 2: Get player profile to find coachId
    const profile = await getPlayerProfile(userInfo.profileId);
    if (!profile?.coachId) {
      // console.log('getCoachByUsername: No coachId found in profile:', userInfo.profileId);
      return null;
    }

    // Step 3: Get the coach document
    const coach = await getCoach(profile.coachId);
    if (!coach) {
      // console.log('getCoachByUsername: No coach found for coachId:', profile.coachId);
      return null;
    }

    return {
      coach,
      playerProfile: profile,
      profileId: userInfo.profileId,
      coachId: profile.coachId,
      username: username.toLowerCase()
    };
  } catch (error) {
    console.error('Error getting coach by username:', error);
    throw error;
  }
};

// ==================== PayPal Coach Functions ====================

/**
 * Set a coach's PayPal email on both playerProfiles and coaches documents.
 * Sets paypalVerified: false on the profile (verified on first successful payment).
 * Also sets acceptsPayPal: true on the coaches doc for marketplace filtering.
 * @param {string} profileId - playerProfiles document ID
 * @param {string} coachId - coaches document ID
 * @param {string} paypalEmail - Coach's PayPal receiving email
 * @returns {Promise<void>}
 */
export const setCoachPayPalEmail = async (profileId, coachId, paypalEmail) => {
  try {
    const profileRef = doc(db, PLAYER_PROFILES_COLLECTION, profileId);
    const coachRef = doc(db, COACHES_COLLECTION, coachId);

    await Promise.all([
      updateDoc(profileRef, {
        paypalEmail,
        paypalVerified: false,
        updatedAt: serverTimestamp(),
      }),
      updateDoc(coachRef, {
        paypalEmail,
        acceptsPayPal: true,
        updatedAt: serverTimestamp(),
      }),
    ]);
  } catch (error) {
    console.error('Error setting coach PayPal email:', error);
    throw error;
  }
};

/**
 * Get PayPal transaction history for a coach.
 * Queries the paypalTransactions collection ordered by createdAt desc.
 * @param {string} coachId - Coach document ID
 * @returns {Promise<Array>} - Array of transaction documents
 */
export const getCoachPayPalRevenue = async (coachId) => {
  try {
    const txRef = collection(db, 'paypalTransactions');
    const q = query(txRef, where('coachId', '==', coachId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting coach PayPal revenue:', error);
    return [];
  }
};

/**
 * Get PayPal invoice history for a coach.
 * Queries the paypalInvoices collection ordered by createdAt desc.
 * @param {string} coachId - Coach document ID
 * @returns {Promise<Array>} - Array of invoice documents
 */
export const getCoachPayPalInvoices = async (coachId) => {
  try {
    const invRef = collection(db, 'paypalInvoices');
    const q = query(invRef, where('coachId', '==', coachId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting coach PayPal invoices:', error);
    return [];
  }
};

/**
 * Get ALL PayPal invoices (admin use).
 * Queries the paypalInvoices collection ordered by createdAt desc.
 * @returns {Promise<Array>} - Array of invoice documents
 */
export const getAllPayPalInvoices = async () => {
  try {
    const invRef = collection(db, 'paypalInvoices');
    const q = query(invRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting all PayPal invoices:', error);
    return [];
  }
};

/**
 * Get PayPal transactions by their document IDs (for invoice detail view).
 * @param {string[]} transactionIds - Array of paypalTransactions doc IDs
 * @returns {Promise<Array>} - Array of transaction documents
 */
export const getPayPalTransactionsByIds = async (transactionIds) => {
  try {
    if (!transactionIds || transactionIds.length === 0) return [];
    // Firestore 'in' queries limited to 30 items, batch if needed
    const results = [];
    const batches = [];
    for (let i = 0; i < transactionIds.length; i += 30) {
      batches.push(transactionIds.slice(i, i + 30));
    }
    for (const batch of batches) {
      const txRef = collection(db, 'paypalTransactions');
      const q = query(txRef, where('__name__', 'in', batch));
      const snapshot = await getDocs(q);
      results.push(...snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    }
    return results;
  } catch (error) {
    console.error('Error getting PayPal transactions by IDs:', error);
    return [];
  }
};

// ============================================
// LESSONS COLLECTION (top-level collection)
// ============================================

const LESSONS_COLLECTION = 'lessons';

/**
 * Create a new lesson for a coach
 * @param {string} coachId - Coach ID
 * @param {Object} lessonData - Lesson data (should include playerProfileId)
 * @returns {Promise<string>} - Returns the created lesson ID
 */
export const createLesson = async (coachId, lessonData) => {
  try {
    // console.log('createLesson called with coachId:', coachId);

    const lessonsRef = collection(db, LESSONS_COLLECTION);
    const lessonDoc = doc(lessonsRef);

    const dataToSave = {
      coachId,
      playerProfileId: lessonData.playerProfileId || '',
      title: lessonData.title || '',
      description: lessonData.description || '',
      level: lessonData.level || 'Intermediate',
      ratingCap: lessonData.ratingCap || null,
      instructor: lessonData.instructor || '',
      duration: lessonData.duration || 60,
      spotsTotal: lessonData.spotsTotal || 10,
      spotsBooked: 0,
      scheduledAtUTC: lessonData.scheduledAtUTC,
      coachTimezone: lessonData.coachTimezone || '',
      displayTimes: lessonData.displayTimes || {},
      status: 'scheduled',
      zoomLink: lessonData.zoomLink || '',
      dailyRoomUrl: lessonData.dailyRoomUrl || '',
      dailyRoomName: lessonData.dailyRoomName || '',
      recordingUrl: '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // console.log('Saving lesson to lessons collection:', dataToSave);
    await setDoc(lessonDoc, dataToSave);
    // console.log('Lesson saved successfully with ID:', lessonDoc.id);

    return lessonDoc.id;
  } catch (error) {
    console.error('Error creating lesson:', error);
    console.error('Error details:', error.message, error.code);
    throw error;
  }
};

/**
 * Get a specific lesson by ID
 * @param {string} lessonId - Lesson ID
 * @returns {Promise<Object|null>} - Returns lesson data or null
 */
export const getLesson = async (lessonId) => {
  try {
    const lessonRef = doc(db, LESSONS_COLLECTION, lessonId);
    const lessonSnap = await getDoc(lessonRef);

    if (lessonSnap.exists()) {
      return {
        id: lessonSnap.id,
        ...lessonSnap.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting lesson:', error);
    throw error;
  }
};

/**
 * Get all lessons for a coach (simple query, no composite index needed)
 * @param {string} coachId - Coach ID
 * @param {Object} options - { limitCount }
 * @returns {Promise<Array>} - Returns array of lessons
 */
export const getLessons = async (coachId, options = {}) => {
  try {
    const lessonsRef = collection(db, LESSONS_COLLECTION);
    let q = query(
      lessonsRef,
      where('coachId', '==', coachId),
      orderBy('scheduledAtUTC', 'asc')
    );

    if (options.limitCount) {
      q = query(q, limit(options.limitCount));
    }

    const querySnapshot = await getDocs(q);
    const lessons = [];

    querySnapshot.forEach((doc) => {
      lessons.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return lessons;
  } catch (error) {
    console.error('Error getting lessons:', error);
    throw error;
  }
};

/**
 * Get upcoming lessons for a coach (scheduled, future dates)
 * Uses simple coachId filter, then filters in memory to avoid complex indexes
 * @param {string} coachId - Coach ID
 * @param {number} limitCount - Maximum number of lessons to return
 * @returns {Promise<Array>} - Returns array of upcoming lessons
 */
export const getUpcomingLessons = async (coachId, limitCount = 10) => {
  try {
    const lessonsRef = collection(db, LESSONS_COLLECTION);

    // Simple query - just filter by coachId and order by date
    const q = query(
      lessonsRef,
      where('coachId', '==', coachId),
      orderBy('scheduledAtUTC', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const now = new Date();
    const lessons = [];

    // Lessons are "past" 1 hour after start time, so "upcoming" includes lessons up to 1 hour old
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Filter in memory: scheduled lessons that haven't ended (1 hr after start)
      const scheduledDate = data.scheduledAtUTC?.toDate ? data.scheduledAtUTC.toDate() : new Date(data.scheduledAtUTC);
      if (data.status === 'scheduled' && scheduledDate > oneHourAgo && data.sessionType !== 'individual') {
        lessons.push({
          id: doc.id,
          ...data
        });
      }
    });

    // Return limited results
    return lessons.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting upcoming lessons:', error);
    throw error;
  }
};

/**
 * Get past lessons for a coach
 * @param {string} coachId - Coach ID
 * @param {number} limitCount - Maximum number of lessons to return
 * @returns {Promise<Array>} - Returns array of past lessons
 */
export const getPastLessons = async (coachId, limitCount = 10) => {
  try {
    const lessonsRef = collection(db, LESSONS_COLLECTION);

    // Simple query - just filter by coachId
    const q = query(
      lessonsRef,
      where('coachId', '==', coachId),
      orderBy('scheduledAtUTC', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const now = new Date();
    const lessons = [];

    // Lessons are "past" 1 hour after start time
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Filter in memory: lessons that ended (1 hr after start)
      const scheduledDate = data.scheduledAtUTC?.toDate ? data.scheduledAtUTC.toDate() : new Date(data.scheduledAtUTC);
      if (scheduledDate <= oneHourAgo) {
        lessons.push({
          id: doc.id,
          ...data
        });
      }
    });

    // Return limited results
    return lessons.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting past lessons:', error);
    throw error;
  }
};

/**
 * Update a lesson
 * @param {string} lessonId - Lesson ID
 * @param {Object} data - Data to update
 * @returns {Promise<void>}
 */
export const updateLesson = async (lessonId, data) => {
  try {
    const lessonRef = doc(db, LESSONS_COLLECTION, lessonId);
    await updateDoc(lessonRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating lesson:', error);
    throw error;
  }
};

/**
 * Cancel a lesson (soft delete)
 * @param {string} lessonId - Lesson ID
 * @returns {Promise<void>}
 */
export const cancelLesson = async (lessonId) => {
  try {
    const lessonRef = doc(db, LESSONS_COLLECTION, lessonId);
    await updateDoc(lessonRef, {
      status: 'cancelled',
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error cancelling lesson:', error);
    throw error;
  }
};

/**
 * Delete a lesson (hard delete)
 * @param {string} lessonId - Lesson ID
 * @returns {Promise<void>}
 */
export const deleteLesson = async (lessonId) => {
  try {
    const lessonRef = doc(db, LESSONS_COLLECTION, lessonId);
    await deleteDoc(lessonRef);
  } catch (error) {
    console.error('Error deleting lesson:', error);
    throw error;
  }
};

/**
 * Book a spot in a lesson (increment spotsBooked)
 * @param {string} lessonId - Lesson ID
 * @returns {Promise<boolean>} - Returns true if booking successful, false if full
 */
export const bookLessonSpot = async (lessonId) => {
  try {
    const lessonRef = doc(db, LESSONS_COLLECTION, lessonId);

    return await runTransaction(db, async (transaction) => {
      const lessonSnap = await transaction.get(lessonRef);

      if (!lessonSnap.exists()) {
        throw new Error('Lesson not found');
      }

      const lessonData = lessonSnap.data();
      const spotsBooked = lessonData.spotsBooked || 0;
      const spotsTotal = lessonData.spotsTotal || 0;

      if (spotsBooked >= spotsTotal) {
        return false; // Lesson is full
      }

      transaction.update(lessonRef, {
        spotsBooked: increment(1),
        updatedAt: serverTimestamp(),
      });

      return true;
    });
  } catch (error) {
    console.error('Error booking lesson spot:', error);
    throw error;
  }
};

/**
 * Get available spots in a lesson
 * @param {string} lessonId - Lesson ID
 * @returns {Promise<number>} - Returns number of available spots
 */
export const getAvailableLessonSpots = async (lessonId) => {
  try {
    const lesson = await getLesson(lessonId);
    if (!lesson) {
      throw new Error('Lesson not found');
    }

    const spotsTotal = lesson.spotsTotal || 0;
    const spotsBooked = lesson.spotsBooked || 0;

    return Math.max(0, spotsTotal - spotsBooked);
  } catch (error) {
    console.error('Error getting available lesson spots:', error);
    throw error;
  }
};

/**
 * Get user's credit balance for a specific coach
 * @param {string} userId - User's Firebase UID
 * @param {string} coachId - Coach ID
 * @returns {Promise<number>} - Returns credit balance (0 if none)
 */
export const getUserCoachCredits = async (userId, coachId) => {
  try {
    if (!userId || !coachId) {
      return 0;
    }

    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return 0;
    }

    const userData = userSnap.data();
    const coachCredits = userData.coachCredits || {};
    const coachData = coachCredits[coachId];

    // Handle both old format (number) and new format (object with credits)
    if (typeof coachData === 'number') {
      return coachData;
    }
    return coachData?.credits || 0;
  } catch (error) {
    console.error('Error getting user coach credits:', error);
    return 0;
  }
};

/**
 * Add credits to a user's account for a specific coach
 * @param {string} userId - User's Firebase UID
 * @param {string} coachId - Coach ID
 * @param {number} amount - Number of credits to add
 * @param {string} [username] - Coach's username (for reference)
 * @returns {Promise<number>} - Returns new credit balance
 */
export const addUserCoachCredits = async (userId, coachId, amount = 1, username = '', userEmail = '', userName = '') => {
  try {
    if (!userId || !coachId) {
      throw new Error('userId and coachId are required');
    }

    const userRef = doc(db, USERS_COLLECTION, userId);
    const studentRef = doc(db, 'coaches', coachId, 'students', userId);

    // Use a transaction to safely increment credits and sync student record
    const newBalance = await runTransaction(db, async (transaction) => {
      const userSnap = await transaction.get(userRef);
      const studentSnap = await transaction.get(studentRef);

      let currentCredits = 0;
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const coachCredits = userData.coachCredits || {};
        const coachData = coachCredits[coachId];
        // Handle both old format (number) and new format (object)
        if (typeof coachData === 'number') {
          currentCredits = coachData;
        } else {
          currentCredits = coachData?.credits || 0;
        }
      }

      const updatedCredits = currentCredits + amount;
      const now = new Date().toISOString();

      // Update the nested coachCredits field with username
      transaction.set(userRef, {
        coachCredits: {
          [coachId]: {
            credits: updatedCredits,
            username: username || null,
            updatedAt: now
          }
        },
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Also sync to coaches/{coachId}/students/{userId} subcollection
      const studentData = {
        odaUserId: userId,
        email: userEmail || '',
        name: userName || username || '',
        credits: updatedCredits,
        hasPurchased: true,
        updatedAt: now
      };

      // If student record doesn't exist, set firstPurchaseAt and lessonsAttended
      if (!studentSnap.exists()) {
        studentData.firstPurchaseAt = now;
        studentData.lessonsAttended = 0;
      }

      transaction.set(studentRef, studentData, { merge: true });

      return updatedCredits;
    });

    return newBalance;
  } catch (error) {
    console.error('Error adding user coach credits:', error);
    throw error;
  }
};

/**
 * Deduct credits from a user's account for a specific coach
 * Used when registering for lessons
 * @param {string} userId - User's Firebase UID
 * @param {string} coachId - Coach ID
 * @param {number} amount - Number of credits to deduct (default 1)
 * @returns {Promise<{creditsBefore: number, creditsAfter: number}>} - Returns balance info
 */
export const deductUserCoachCredits = async (userId, coachId, amount = 1) => {
  try {
    if (!userId || !coachId) {
      throw new Error('userId and coachId are required');
    }

    const userRef = doc(db, USERS_COLLECTION, userId);
    const studentRef = doc(db, 'coaches', coachId, 'students', userId);

    const result = await runTransaction(db, async (transaction) => {
      const userSnap = await transaction.get(userRef);
      const studentSnap = await transaction.get(studentRef);

      let currentCredits = 0;
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const coachCredits = userData.coachCredits || {};
        const coachData = coachCredits[coachId];
        if (typeof coachData === 'number') {
          currentCredits = coachData;
        } else {
          currentCredits = coachData?.credits || 0;
        }
      }

      if (currentCredits < amount) {
        throw new Error('Insufficient credits');
      }

      const updatedCredits = currentCredits - amount;
      const now = new Date().toISOString();

      // Get existing username for the coach entry
      const existingCoachData = userSnap.exists() ? userSnap.data().coachCredits?.[coachId] : {};
      const username = typeof existingCoachData === 'object' ? existingCoachData.username : null;

      // Update the nested coachCredits field
      transaction.set(userRef, {
        coachCredits: {
          [coachId]: {
            credits: updatedCredits,
            username: username || null,
            updatedAt: now
          }
        },
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Also sync to coaches/{coachId}/students/{userId} subcollection
      if (studentSnap.exists()) {
        transaction.update(studentRef, {
          credits: updatedCredits,
          updatedAt: now
        });
      }

      return { creditsBefore: currentCredits, creditsAfter: updatedCredits };
    });

    return result;
  } catch (error) {
    console.error('Error deducting user coach credits:', error);
    throw error;
  }
};

/**
 * Smart credit deduction: consumes membership credits first, then purchased credits.
 * Dual-syncs both user doc and student subcollection.
 *
 * @param {string} userId - User's Firebase UID
 * @param {string} coachId - Coach ID
 * @param {number} amount - Credits to deduct (default 1)
 * @returns {{ membershipDeducted: number, purchasedDeducted: number, membershipRemaining: number, purchasedRemaining: number }}
 */
export const deductCreditSmart = async (userId, coachId, amount = 1) => {
  try {
    if (!userId || !coachId) {
      throw new Error('userId and coachId are required');
    }

    const userRef = doc(db, USERS_COLLECTION, userId);
    const studentRef = doc(db, 'coaches', coachId, 'students', userId);

    const result = await runTransaction(db, async (transaction) => {
      const userSnap = await transaction.get(userRef);
      const studentSnap = await transaction.get(studentRef);

      // Read purchased credits from user doc
      let purchasedCredits = 0;
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const coachData = userData.coachCredits?.[coachId];
        if (typeof coachData === 'number') {
          purchasedCredits = coachData;
        } else {
          purchasedCredits = coachData?.credits || 0;
        }
      }

      // Read membership credits from student doc
      let membershipCredits = 0;
      if (studentSnap.exists()) {
        const studentData = studentSnap.data();
        // Only count membership credits if period hasn't expired
        if (studentData.isMember && studentData.membershipCurrentPeriodEnd) {
          if (new Date(studentData.membershipCurrentPeriodEnd) > new Date()) {
            membershipCredits = studentData.membershipCredits || 0;
          }
        }
      }

      const totalAvailable = membershipCredits + purchasedCredits;
      if (totalAvailable < amount) {
        throw new Error('Insufficient credits');
      }

      // Deduct from membership first, remainder from purchased
      let membershipDeducted = Math.min(membershipCredits, amount);
      let purchasedDeducted = amount - membershipDeducted;

      const newMembership = membershipCredits - membershipDeducted;
      const newPurchased = purchasedCredits - purchasedDeducted;
      const now = new Date().toISOString();

      // Get existing username for the coach entry
      const existingCoachData = userSnap.exists() ? userSnap.data().coachCredits?.[coachId] : {};
      const username = typeof existingCoachData === 'object' ? existingCoachData.username : null;

      // Update user doc (purchased credits)
      transaction.set(userRef, {
        coachCredits: {
          [coachId]: {
            credits: newPurchased,
            membershipCredits: newMembership,
            username: username || null,
            updatedAt: now,
          }
        },
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Update student record
      if (studentSnap.exists()) {
        transaction.update(studentRef, {
          credits: newPurchased,
          membershipCredits: newMembership,
          updatedAt: now,
        });
      }

      return {
        membershipDeducted,
        purchasedDeducted,
        membershipRemaining: newMembership,
        purchasedRemaining: newPurchased,
      };
    });

    return result;
  } catch (error) {
    console.error('Error in deductCreditSmart:', error);
    throw error;
  }
};

/**
 * Get a user's membership credits for a specific coach
 * @param {string} userId - User's Firebase UID
 * @param {string} coachId - Coach ID
 * @returns {{ membershipCredits: number, membershipCurrentPeriodEnd: string|null, isMember: boolean }}
 */
export const getUserMembershipCredits = async (userId, coachId) => {
  try {
    if (!userId || !coachId) return { membershipCredits: 0, membershipCurrentPeriodEnd: null, isMember: false };

    const studentRef = doc(db, 'coaches', coachId, 'students', userId);
    const studentSnap = await getDoc(studentRef);

    if (!studentSnap.exists()) {
      return { membershipCredits: 0, membershipCurrentPeriodEnd: null, isMember: false };
    }

    const data = studentSnap.data();
    return {
      membershipCredits: data.membershipCredits || 0,
      membershipCurrentPeriodEnd: data.membershipCurrentPeriodEnd || null,
      isMember: data.isMember === true,
      freeTrialClaimed: data.freeTrialClaimed === true,
    };
  } catch (error) {
    console.error('Error getting membership credits:', error);
    return { membershipCredits: 0, membershipCurrentPeriodEnd: null, isMember: false };
  }
};

/**
 * Refund credits to a user's account for a specific coach
 * Used when cancelling lesson registrations
 * @param {string} userId - User's Firebase UID
 * @param {string} coachId - Coach ID
 * @param {number} amount - Number of credits to refund (default 1)
 * @returns {Promise<{creditsBefore: number, creditsAfter: number}>} - Returns balance info
 */
export const refundUserCoachCredits = async (userId, coachId, amount = 1) => {
  try {
    if (!userId || !coachId) {
      throw new Error('userId and coachId are required');
    }

    const userRef = doc(db, USERS_COLLECTION, userId);
    const studentRef = doc(db, 'coaches', coachId, 'students', userId);

    const result = await runTransaction(db, async (transaction) => {
      const userSnap = await transaction.get(userRef);
      const studentSnap = await transaction.get(studentRef);

      let currentCredits = 0;
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const coachCredits = userData.coachCredits || {};
        const coachData = coachCredits[coachId];
        if (typeof coachData === 'number') {
          currentCredits = coachData;
        } else {
          currentCredits = coachData?.credits || 0;
        }
      }

      const updatedCredits = currentCredits + amount;
      const now = new Date().toISOString();

      // Get existing username for the coach entry
      const existingCoachData = userSnap.exists() ? userSnap.data().coachCredits?.[coachId] : {};
      const username = typeof existingCoachData === 'object' ? existingCoachData.username : null;

      // Update the nested coachCredits field
      transaction.set(userRef, {
        coachCredits: {
          [coachId]: {
            credits: updatedCredits,
            username: username || null,
            updatedAt: now
          }
        },
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Also sync to coaches/{coachId}/students/{userId} subcollection
      if (studentSnap.exists()) {
        transaction.update(studentRef, {
          credits: updatedCredits,
          updatedAt: now
        });
      }

      return { creditsBefore: currentCredits, creditsAfter: updatedCredits };
    });

    return result;
  } catch (error) {
    console.error('Error refunding user coach credits:', error);
    throw error;
  }
};

/**
 * Create an audit log entry for credit operations
 * @param {Object} logData - Audit log data
 * @param {string} logData.type - 'purchase' | 'registration' | 'cancellation' | 'coach_refund' | 'admin_adjustment'
 * @param {string} logData.userId - User's Firebase UID
 * @param {string} logData.coachId - Coach ID
 * @param {number} logData.creditsBefore - Balance before operation
 * @param {number} logData.creditsAfter - Balance after operation
 * @param {number} logData.creditsChanged - Amount changed (positive or negative)
 * @param {Object} [logData.userSnapshot] - User info snapshot
 * @param {Object} [logData.purchaseData] - Purchase-specific data
 * @param {Object} [logData.lessonData] - Lesson-specific data
 * @param {Object} [logData.adminData] - Admin adjustment data
 * @returns {Promise<string>} - Returns the audit log document ID
 */
export const createCreditAuditLog = async (logData) => {
  try {
    const auditLogsRef = collection(db, 'creditAuditLogs');
    const docRef = await addDoc(auditLogsRef, {
      ...logData,
      timestamp: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating credit audit log:', error);
    // Don't throw - audit logging failures shouldn't break the main operation
    return null;
  }
};

/**
 * Get coach earnings from creditAuditLogs collection
 * Queries purchases for a specific coach and calculates stats
 * @param {string} coachId - The coach's Firestore document ID
 * @param {number} [limit=20] - Maximum number of recent payments to return
 * @returns {Promise<Object>} - { recentPayments, thisWeek, thisMonth, totalTransactions }
 */
export const getCoachEarnings = async (coachId, limitCount = 20) => {
  try {
    if (!coachId) {
      throw new Error('coachId is required');
    }

    // Query creditAuditLogs for purchases for this coach
    const auditLogsRef = collection(db, 'creditAuditLogs');
    const purchaseQuery = query(
      auditLogsRef,
      where('coachId', '==', coachId),
      where('type', '==', 'purchase'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(purchaseQuery);

    // Calculate time boundaries
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Start of current week (Sunday)
    startOfWeek.setHours(0, 0, 0, 0);

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0);

    let thisWeek = 0;
    let thisMonth = 0;
    let totalTransactions = 0;

    const recentPayments = snapshot.docs.map(doc => {
      const data = doc.data();
      const timestamp = data.timestamp?.toDate?.() || new Date(data.timestamp);
      const coachNet = data.purchaseData?.coachNetUsd || 0;

      // Calculate stats
      totalTransactions++;
      if (timestamp >= startOfWeek) {
        thisWeek += coachNet;
      }
      if (timestamp >= startOfMonth) {
        thisMonth += coachNet;
      }

      // Format date for display
      const dateStr = timestamp.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

      // Determine package label
      const packageType = data.purchaseData?.packageType || '1';
      const packageLabel = packageType === '1' ? '1 Session' :
                          packageType === '4' ? '4 Sessions' :
                          packageType === '7' ? '7 Sessions' : `${packageType} Sessions`;

      return {
        id: doc.id,
        name: data.userSnapshot?.name || 'Unknown',
        package: packageLabel,
        amount: coachNet,
        date: dateStr,
        status: 'completed',
        timestamp: timestamp,
      };
    });

    return {
      recentPayments,
      thisWeek: Math.round(thisWeek * 100) / 100,
      thisMonth: Math.round(thisMonth * 100) / 100,
      totalTransactions,
    };
  } catch (error) {
    console.error('Error getting coach earnings:', error);
    // Return empty data on error
    return {
      recentPayments: [],
      thisWeek: 0,
      thisMonth: 0,
      totalTransactions: 0,
    };
  }
};

/**
 * Get aggregated revenue data from all payment sources
 * @param {Object} params
 * @param {string} params.coachId - Coach document ID (for creditAuditLogs)
 * @param {string} params.profileId - Player profile ID (for donations)
 * @param {string} params.userId - User UID (for shopOrders, events)
 * @param {number} params.limitCount - Max recent transactions per source
 * @returns {Promise<Object>} - Revenue data with breakdowns by source and time period
 */
export const getCoachRevenue = async ({ coachId, profileId, userId, limitCount = 50 } = {}) => {
  try {
    // Calculate time boundaries
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0);

    let allTime = 0;
    let thisMonth = 0;
    let thisWeek = 0;
    let totalTransactions = 0;
    const bySource = {};
    let allTransactions = [];

    const addTx = (tx) => {
      totalTransactions++;
      allTime += tx.amount;
      if (tx.timestamp >= startOfWeek) thisWeek += tx.amount;
      if (tx.timestamp >= startOfMonth) thisMonth += tx.amount;
      bySource[tx.type] = (bySource[tx.type] || 0) + tx.amount;
      allTransactions.push(tx);
    };

    const formatDate = (ts) => ts.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });

    // --- 1. Credit Audit Logs (credits, 1-on-1 sessions, memberships) ---
    if (coachId) {
      try {
        const auditSnap = await getDocs(query(
          collection(db, 'creditAuditLogs'),
          where('coachId', '==', coachId),
          orderBy('timestamp', 'desc'),
          limit(limitCount)
        ));

        auditSnap.docs.forEach(docSnap => {
          const data = docSnap.data();
          const type = data.type;
          if (!['purchase', 'individual_session_booking', 'membership_grant', 'membership_renewal'].includes(type)) return;

          const timestamp = data.timestamp?.toDate?.() || new Date(data.timestamp);
          let coachNet = 0;
          let sourceKey = 'credits';
          const name = data.userSnapshot?.name || data.userSnapshot?.email || 'Unknown';

          if (type === 'purchase') {
            coachNet = data.purchaseData?.coachNetUsd || 0;
            sourceKey = 'credits';
          } else if (type === 'individual_session_booking') {
            coachNet = data.paymentData?.coachNetUsd || 0;
            sourceKey = 'individual_session';
          } else if (type === 'membership_grant' || type === 'membership_renewal') {
            coachNet = data.paymentData?.coachNetUsd || data.amountUsd || 0;
            sourceKey = 'membership';
          }

          addTx({ id: docSnap.id, type: sourceKey, name, amount: coachNet, date: formatDate(timestamp), timestamp });
        });
      } catch (err) {
        console.error('Revenue: creditAuditLogs query failed:', err);
      }
    }

    // --- 2. Donations ---
    if (profileId) {
      try {
        const donationSnap = await getDocs(query(
          collection(db, 'donations'),
          where('profileId', '==', profileId),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        ));

        donationSnap.docs.forEach(docSnap => {
          const data = docSnap.data();
          const timestamp = data.createdAt?.toDate?.() || new Date(data.createdAt);
          // Donations store amounts in cents
          const vendorProfit = (data.vendorProfit || 0) / 100;
          const name = data.donorEmail || 'Anonymous';

          addTx({ id: docSnap.id, type: 'donation', name, amount: vendorProfit, date: formatDate(timestamp), timestamp });
        });
      } catch (err) {
        console.error('Revenue: donations query failed:', err);
      }
    }

    // --- 3. Shop Orders ---
    if (userId) {
      try {
        const shopSnap = await getDocs(query(
          collection(db, 'shopOrders'),
          where('vendor_info.id', '==', userId),
          orderBy('created_at', 'desc'),
          limit(limitCount)
        ));

        shopSnap.docs.forEach(docSnap => {
          const data = docSnap.data();
          const timestamp = data.created_at?.toDate?.() || new Date(data.created_at);
          // Shop orders store pricing in cents
          const vendorPayout = (data.pricing?.vendor_payout || 0) / 100;
          const name = data.buyer_info?.name || data.buyer_info?.email || 'Unknown';

          addTx({ id: docSnap.id, type: 'shop', name, amount: vendorPayout, date: formatDate(timestamp), timestamp });
        });
      } catch (err) {
        console.error('Revenue: shopOrders query failed:', err);
      }
    }

    // --- 4. Tournament Registrations (eventPayments via user's events) ---
    if (userId) {
      try {
        const eventsSnap = await getDocs(query(
          collection(db, 'events'),
          where('organizerId', '==', userId)
        ));

        const eventIds = eventsSnap.docs.map(d => d.id);

        // Firestore 'in' queries support max 30 values
        for (let i = 0; i < eventIds.length; i += 30) {
          const batch = eventIds.slice(i, i + 30);
          const paymentsSnap = await getDocs(query(
            collection(db, 'eventPayments'),
            where('eventId', 'in', batch),
            orderBy('paymentDate', 'desc'),
            limit(limitCount)
          ));

          paymentsSnap.docs.forEach(docSnap => {
            const data = docSnap.data();
            const timestamp = data.paymentDate?.toDate?.() || new Date(data.paymentDate);
            // eventPayments store amounts in dollars already
            const netAmount = data.netAmountActual || data.netAmount || 0;
            const name = data.participantName || data.participantEmail || 'Unknown';

            addTx({ id: docSnap.id, type: 'tournament', name, amount: netAmount, date: formatDate(timestamp), timestamp });
          });
        }
      } catch (err) {
        console.error('Revenue: eventPayments query failed:', err);
      }
    }

    // Sort all transactions by date descending
    allTransactions.sort((a, b) => b.timestamp - a.timestamp);

    return {
      allTime: Math.round(allTime * 100) / 100,
      thisMonth: Math.round(thisMonth * 100) / 100,
      thisWeek: Math.round(thisWeek * 100) / 100,
      totalTransactions,
      bySource: Object.fromEntries(
        Object.entries(bySource).map(([k, v]) => [k, Math.round(v * 100) / 100])
      ),
      recentTransactions: allTransactions,
    };
  } catch (error) {
    console.error('Error getting coach revenue:', error);
    return {
      allTime: 0,
      thisMonth: 0,
      thisWeek: 0,
      totalTransactions: 0,
      bySource: {},
      recentTransactions: [],
    };
  }
};

/**
 * Get all students for a coach from the coaches/{coachId}/students subcollection
 * @param {string} coachId - The coach's Firestore document ID
 * @returns {Promise<Array>} - Array of student objects
 */
export const getCoachStudents = async (coachId) => {
  try {
    if (!coachId) {
      throw new Error('coachId is required');
    }

    const studentsRef = collection(db, 'coaches', coachId, 'students');
    const snapshot = await getDocs(studentsRef);

    // Fetch user documents to get email addresses
    const students = await Promise.all(
      snapshot.docs.map(async (studentDoc) => {
        const studentData = studentDoc.data();
        const userId = studentDoc.id;

        // Fetch user document for email
        let email = studentData.email || '';
        if (!email) {
          try {
            const userRef = doc(db, USERS_COLLECTION, userId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              email = userSnap.data().email || '';
            }
          } catch (e) {
            console.warn('Could not fetch user email for', userId);
          }
        }

        return {
          id: userId,
          ...studentData,
          email
        };
      })
    );

    // Sort by credits descending, then by name
    students.sort((a, b) => {
      if (b.credits !== a.credits) return b.credits - a.credits;
      return (a.name || '').localeCompare(b.name || '');
    });

    return students;
  } catch (error) {
    console.error('Error getting coach students:', error);
    throw error;
  }
};

/**
 * Register a user for a lesson (add to participants array)
 * Deducts 1 credit from the user's balance and creates an audit log
 * @param {string} lessonId - Lesson ID
 * @param {Object} participantData - Participant data { odaUserId, email, name }
 * @returns {Promise<boolean>} - Returns true if successful
 */
export const registerForLesson = async (lessonId, participantData) => {
  try {
    const lessonRef = doc(db, LESSONS_COLLECTION, lessonId);

    // First, get the lesson to get coachId and verify enrollment
    const lessonSnap = await getDoc(lessonRef);
    if (!lessonSnap.exists()) {
      throw new Error('Lesson not found');
    }

    const lessonData = lessonSnap.data();
    const coachId = lessonData.coachId;

    if (!coachId) {
      throw new Error('Lesson has no associated coach');
    }

    const userId = participantData.odaUserId;

    // Check current credit balance (purchased + membership)
    const currentCredits = await getUserCoachCredits(userId, coachId);
    const membershipInfo = await getUserMembershipCredits(userId, coachId);
    const totalCredits = currentCredits + (membershipInfo.membershipCredits || 0);
    if (totalCredits < 1) {
      throw new Error('Insufficient credits. Please purchase more credits to register.');
    }

    // Check credit expiration — ensure at least one non-expired purchase or active membership exists
    if (!membershipInfo.isMember) {
      const purchaseHistory = await getCreditPurchaseHistory(userId, coachId);
      const hasValidCredits = purchaseHistory.some(p => !p.expired);
      if (purchaseHistory.length > 0 && !hasValidCredits && currentCredits > 0) {
        throw new Error('All credits have expired. Please purchase new credits to register.');
      }
    }

    // Use transaction for atomic registration and smart credit deduction
    const result = await runTransaction(db, async (transaction) => {
      const freshLessonSnap = await transaction.get(lessonRef);

      if (!freshLessonSnap.exists()) {
        throw new Error('Lesson not found');
      }

      const freshLessonData = freshLessonSnap.data();
      const participants = freshLessonData.participants || [];

      // Check if user is already enrolled
      const alreadyEnrolled = participants.some(p => p.odaUserId === userId);
      if (alreadyEnrolled) {
        throw new Error('User is already enrolled in this lesson');
      }

      // Check if there are spots available
      const spotsBooked = freshLessonData.spotsBooked || 0;
      const spotsTotal = freshLessonData.spotsTotal || 10;
      if (spotsBooked >= spotsTotal) {
        throw new Error('No spots available');
      }

      // Get and update user's credits (dual pool: membership first, then purchased)
      const userRef = doc(db, USERS_COLLECTION, userId);
      const studentRef = doc(db, 'coaches', coachId, 'students', userId);
      const userSnap = await transaction.get(userRef);
      const studentSnap = await transaction.get(studentRef);

      // Read purchased credits
      let purchasedCredits = 0;
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const coachData = userData.coachCredits?.[coachId];
        if (typeof coachData === 'number') {
          purchasedCredits = coachData;
        } else {
          purchasedCredits = coachData?.credits || 0;
        }
      }

      // Read membership credits
      let memCredits = 0;
      if (studentSnap.exists()) {
        const sData = studentSnap.data();
        if (sData.isMember && sData.membershipCurrentPeriodEnd) {
          if (new Date(sData.membershipCurrentPeriodEnd) > new Date()) {
            memCredits = sData.membershipCredits || 0;
          }
        }
      }

      const totalAvailable = memCredits + purchasedCredits;
      if (totalAvailable < 1) {
        throw new Error('Insufficient credits');
      }

      // Deduct from membership first, remainder from purchased
      const membershipDeducted = Math.min(memCredits, 1);
      const purchasedDeducted = 1 - membershipDeducted;
      const newMembership = memCredits - membershipDeducted;
      const newPurchased = purchasedCredits - purchasedDeducted;
      const creditsBefore = purchasedCredits;
      const creditsAfter = newPurchased;
      const now = new Date().toISOString();

      // Get existing username for the coach entry
      const existingCoachData = userSnap.exists() ? userSnap.data().coachCredits?.[coachId] : {};
      const username = typeof existingCoachData === 'object' ? existingCoachData.username : null;

      // Update user doc with both credit pools
      transaction.set(userRef, {
        coachCredits: {
          [coachId]: {
            credits: newPurchased,
            membershipCredits: newMembership,
            username: username || null,
            updatedAt: now
          }
        },
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Update student record
      if (studentSnap.exists()) {
        transaction.update(studentRef, {
          credits: newPurchased,
          membershipCredits: newMembership,
          updatedAt: now
        });
      }

      // Add participant with registration timestamp and credit consumption timestamp
      const newParticipant = {
        ...participantData,
        registeredAt: now,
        creditsConsumedAt: now,
        creditsRemaining: newPurchased + newMembership,
        membershipDeducted,
        purchasedDeducted,
      };

      transaction.update(lessonRef, {
        participants: [...participants, newParticipant],
        spotsBooked: increment(1),
        updatedAt: serverTimestamp()
      });

      return { creditsBefore, creditsAfter, membershipDeducted, purchasedDeducted, lessonTitle: freshLessonData.title, scheduledAtUTC: freshLessonData.scheduledAtUTC, registrationCount: participants.length + 1, duration: freshLessonData.duration || 60 };
    });

    // Create audit log entry (non-blocking)
    createCreditAuditLog({
      type: result.membershipDeducted > 0 ? 'membership_deduct' : 'registration',
      userId: userId,
      coachId: coachId,
      creditsBefore: result.creditsBefore,
      creditsAfter: result.creditsAfter,
      creditsChanged: -1,
      membershipDeducted: result.membershipDeducted || 0,
      purchasedDeducted: result.purchasedDeducted || 0,
      userSnapshot: {
        email: participantData.email || '',
        name: participantData.name || '',
      },
      lessonData: {
        lessonId: lessonId,
        lessonTitle: result.lessonTitle || '',
        scheduledAtUTC: result.scheduledAtUTC || null,
      },
    }).catch(err => console.error('Failed to create audit log:', err));

    // Send registration confirmation email via n8n (non-blocking)
    (async () => {
      try {
        // Get coach info for the email (including coach email for n8n notifications)
        let coachName = '';
        let coachUsername = '';
        let coachEmail = '';
        const coachSnap = await getDoc(doc(db, 'coaches', coachId));
        if (coachSnap.exists()) {
          const coachData = coachSnap.data();
          coachName = coachData.name || coachData.displayName || '';
          coachUsername = coachData.username || '';
          coachEmail = coachData.email || '';

          // If no email on coach doc, try to get from linked playerProfile
          if (!coachEmail && coachData.playerProfileId) {
            try {
              const profileSnap = await getDoc(doc(db, 'playerProfiles', coachData.playerProfileId));
              if (profileSnap.exists()) {
                coachEmail = profileSnap.data().email || '';
              }
            } catch (e) {
              console.warn('Could not fetch playerProfile for coach email');
            }
          }

          // Last resort: try users collection with userId
          if (!coachEmail && coachData.userId) {
            try {
              const userSnap2 = await getDoc(doc(db, USERS_COLLECTION, coachData.userId));
              if (userSnap2.exists()) {
                coachEmail = userSnap2.data().email || '';
              }
            } catch (e) {
              console.warn('Could not fetch user for coach email');
            }
          }
        }

        const scheduledAtUTC = result.scheduledAtUTC?.toDate?.()
          ? result.scheduledAtUTC.toDate().toISOString()
          : result.scheduledAtUTC;

        const functionsUrl = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-chess-web-host.cloudfunctions.net';
        await fetch(`${functionsUrl}/sendGroupChessRegistrationEmail`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            studentEmail: participantData.email || '',
            studentName: participantData.name || '',
            lessonId: lessonId,
            lessonTitle: result.lessonTitle || '',
            scheduledAtUTC: scheduledAtUTC,
            coachName: coachName,
            coachUsername: coachUsername,
            coachEmail: coachEmail,
            registrationCount: result.registrationCount || 0,
            duration: result.duration || 60,
          }),
        });
      } catch (err) {
        console.error('Failed to send registration email:', err);
        // Don't throw - email is non-critical
      }
    })();

    return true;
  } catch (error) {
    console.error('Error registering for lesson:', error);
    throw error;
  }
};

/**
 * Cancel a user's registration for a lesson (remove from participants array)
 * Refunds 1 credit if cancellation is >4 hours before lesson start
 * @param {string} lessonId - Lesson ID
 * @param {string} odaUserId - User's Firebase UID to remove
 * @param {boolean} [bypassTimeCheck=false] - Skip the 4-hour check (for coach cancellations)
 * @returns {Promise<{success: boolean, refunded: boolean}>} - Returns success and refund status
 */
export const cancelLessonRegistration = async (lessonId, odaUserId, bypassTimeCheck = false) => {
  try {
    if (!lessonId || !odaUserId) {
      throw new Error('lessonId and odaUserId are required');
    }

    const lessonRef = doc(db, LESSONS_COLLECTION, lessonId);

    // First, get the lesson to check the cancellation window
    const lessonSnap = await getDoc(lessonRef);
    if (!lessonSnap.exists()) {
      throw new Error('Lesson not found');
    }

    const lessonData = lessonSnap.data();
    const coachId = lessonData.coachId;
    const scheduledAtUTC = lessonData.scheduledAtUTC;

    // Check 4-hour cancellation window (unless bypassed for coach cancellations)
    if (!bypassTimeCheck && scheduledAtUTC) {
      const lessonTime = scheduledAtUTC.toDate ? scheduledAtUTC.toDate() : new Date(scheduledAtUTC);
      const now = new Date();
      const fourHoursMs = 4 * 60 * 60 * 1000;
      const timeDiff = lessonTime.getTime() - now.getTime();

      if (timeDiff < fourHoursMs) {
        throw new Error('Cannot cancel registration less than 4 hours before lesson start. Please contact the coach for assistance.');
      }
    }

    // Find the participant to get their info for the audit log
    const participants = lessonData.participants || [];
    const participant = participants.find(p => p.odaUserId === odaUserId);

    if (!participant) {
      throw new Error('User is not registered for this lesson');
    }

    // Use transaction for atomic removal and credit refund
    const result = await runTransaction(db, async (transaction) => {
      const freshLessonSnap = await transaction.get(lessonRef);

      if (!freshLessonSnap.exists()) {
        throw new Error('Lesson not found');
      }

      const freshLessonData = freshLessonSnap.data();
      const freshParticipants = freshLessonData.participants || [];

      // Find the participant to remove
      const participantIndex = freshParticipants.findIndex(p => p.odaUserId === odaUserId);

      if (participantIndex === -1) {
        throw new Error('User is not registered for this lesson');
      }

      // Get and update user's credits (refund 1 credit)
      const userRef = doc(db, USERS_COLLECTION, odaUserId);
      const studentRef = doc(db, 'coaches', coachId, 'students', odaUserId);
      const userSnap = await transaction.get(userRef);
      const studentSnap = await transaction.get(studentRef);

      let creditsBefore = 0;
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const coachCredits = userData.coachCredits || {};
        const coachData = coachCredits[coachId];
        if (typeof coachData === 'number') {
          creditsBefore = coachData;
        } else {
          creditsBefore = coachData?.credits || 0;
        }
      }

      const creditsAfter = creditsBefore + 1;
      const now = new Date().toISOString();

      // Get existing username for the coach entry
      const existingCoachData = userSnap.exists() ? userSnap.data().coachCredits?.[coachId] : {};
      const username = typeof existingCoachData === 'object' ? existingCoachData.username : null;

      // Refund credit to user
      transaction.set(userRef, {
        coachCredits: {
          [coachId]: {
            credits: creditsAfter,
            username: username || null,
            updatedAt: now
          }
        },
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Update student record credits
      if (studentSnap.exists()) {
        transaction.update(studentRef, {
          credits: creditsAfter,
          updatedAt: now
        });
      }

      // Remove only that specific participant
      const updatedParticipants = [
        ...freshParticipants.slice(0, participantIndex),
        ...freshParticipants.slice(participantIndex + 1)
      ];

      // Update the lesson with the filtered participants array and decrement spotsBooked
      const currentSpotsBooked = freshLessonData.spotsBooked || 0;
      transaction.update(lessonRef, {
        participants: updatedParticipants,
        spotsBooked: Math.max(0, currentSpotsBooked - 1),
        updatedAt: serverTimestamp()
      });

      return {
        creditsBefore,
        creditsAfter,
        lessonTitle: freshLessonData.title,
        scheduledAtUTC: freshLessonData.scheduledAtUTC,
      };
    });

    // Create audit log entry (non-blocking)
    createCreditAuditLog({
      type: 'cancellation',
      userId: odaUserId,
      coachId: coachId,
      creditsBefore: result.creditsBefore,
      creditsAfter: result.creditsAfter,
      creditsChanged: 1,
      userSnapshot: {
        email: participant.email || '',
        name: participant.name || '',
      },
      lessonData: {
        lessonId: lessonId,
        lessonTitle: result.lessonTitle || '',
        scheduledAtUTC: result.scheduledAtUTC || null,
      },
    }).catch(err => console.error('Failed to create audit log:', err));

    // Send cancellation notifications via n8n (non-blocking)
    (async () => {
      try {
        // Get coach info (3-level fallback: coaches → playerProfiles → users)
        let coachName = '';
        let coachUsername = '';
        let coachEmail = '';
        const coachSnap = await getDoc(doc(db, 'coaches', coachId));
        if (coachSnap.exists()) {
          const coachData = coachSnap.data();
          coachName = coachData.name || coachData.displayName || '';
          coachUsername = coachData.username || '';
          coachEmail = coachData.email || '';

          if (!coachEmail && coachData.playerProfileId) {
            try {
              const profileSnap = await getDoc(doc(db, 'playerProfiles', coachData.playerProfileId));
              if (profileSnap.exists()) coachEmail = profileSnap.data().email || '';
            } catch (e) { /* ignore */ }
          }
          if (!coachEmail && coachData.userId) {
            try {
              const userSnap2 = await getDoc(doc(db, USERS_COLLECTION, coachData.userId));
              if (userSnap2.exists()) coachEmail = userSnap2.data().email || '';
            } catch (e) { /* ignore */ }
          }
        }

        const functionsUrl = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-chess-web-host.cloudfunctions.net';
        const basePayload = {
          type: 'groupchess_cancellation',
          classType: 'group',
          timestamp: new Date().toISOString(),
          student: { email: participant.email || '', name: participant.name || '' },
          coach: { email: coachEmail, name: coachName, username: coachUsername },
          lesson: {
            id: lessonId,
            title: result.lessonTitle || '',
            scheduledAtUTC: result.scheduledAtUTC?.toDate?.()
              ? result.scheduledAtUTC.toDate().toISOString()
              : result.scheduledAtUTC || null,
          },
          cancellation: { reason: 'student_cancelled', creditsRefunded: 1 },
        };

        // Student cancellation with calendarUid
        await fetch(`${functionsUrl}/sendCoachingNotification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...basePayload,
            recipientRole: 'student',
            calendarUid: `group-${lessonId}-${participant.email}`,
          }),
        });
        // Coach cancellation
        await fetch(`${functionsUrl}/sendCoachingNotification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...basePayload,
            recipientRole: 'coach',
          }),
        });
      } catch (err) {
        console.error('Failed to send cancellation notifications:', err);
      }
    })();

    return { success: true, refunded: true };
  } catch (error) {
    console.error('Error cancelling lesson registration:', error);
    throw error;
  }
};

/**
 * Cancel a lesson (coach action) and refund all registered participants
 * @param {string} lessonId - Lesson ID to cancel
 * @param {string} coachId - Coach ID (for verification)
 * @returns {Promise<{success: boolean, refundedCount: number}>} - Returns success and count of refunded participants
 */
export const coachCancelLesson = async (lessonId, coachId) => {
  try {
    if (!lessonId || !coachId) {
      throw new Error('lessonId and coachId are required');
    }

    const lessonRef = doc(db, LESSONS_COLLECTION, lessonId);
    const lessonSnap = await getDoc(lessonRef);

    if (!lessonSnap.exists()) {
      throw new Error('Lesson not found');
    }

    const lessonData = lessonSnap.data();

    // Verify this is the coach's lesson
    if (lessonData.coachId !== coachId) {
      throw new Error('Not authorized to cancel this lesson');
    }

    const participants = lessonData.participants || [];
    // Save participants before refund loop (participants array gets cleared at lesson cancel)
    const savedParticipants = participants.map(p => ({ ...p }));
    const refundResults = [];

    // Refund each participant
    for (const participant of participants) {
      const userId = participant.odaUserId;

      if (!userId) {
        console.warn('Skipping participant with no userId:', participant);
        continue;
      }

      try {
        // Get current credits
        const userRef = doc(db, USERS_COLLECTION, userId);
        const studentRef = doc(db, 'coaches', coachId, 'students', userId);

        await runTransaction(db, async (transaction) => {
          const userSnap = await transaction.get(userRef);
          const studentSnap = await transaction.get(studentRef);

          let creditsBefore = 0;
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const coachCredits = userData.coachCredits || {};
            const coachData = coachCredits[coachId];
            if (typeof coachData === 'number') {
              creditsBefore = coachData;
            } else {
              creditsBefore = coachData?.credits || 0;
            }
          }

          const creditsAfter = creditsBefore + 1;
          const now = new Date().toISOString();

          // Get existing username for the coach entry
          const existingCoachData = userSnap.exists() ? userSnap.data().coachCredits?.[coachId] : {};
          const username = typeof existingCoachData === 'object' ? existingCoachData.username : null;

          // Refund credit to user
          transaction.set(userRef, {
            coachCredits: {
              [coachId]: {
                credits: creditsAfter,
                username: username || null,
                updatedAt: now
              }
            },
            updatedAt: serverTimestamp()
          }, { merge: true });

          // Update student record credits
          if (studentSnap.exists()) {
            transaction.update(studentRef, {
              credits: creditsAfter,
              updatedAt: now
            });
          }

          refundResults.push({
            userId,
            creditsBefore,
            creditsAfter,
            email: participant.email,
            name: participant.name,
          });
        });

        // Create audit log entry for each refund
        createCreditAuditLog({
          type: 'coach_refund',
          userId: userId,
          coachId: coachId,
          creditsBefore: refundResults[refundResults.length - 1].creditsBefore,
          creditsAfter: refundResults[refundResults.length - 1].creditsAfter,
          creditsChanged: 1,
          userSnapshot: {
            email: participant.email || '',
            name: participant.name || '',
          },
          lessonData: {
            lessonId: lessonId,
            lessonTitle: lessonData.title || '',
            scheduledAtUTC: lessonData.scheduledAtUTC || null,
            reason: 'coach_cancelled',
          },
        }).catch(err => console.error('Failed to create audit log:', err));

      } catch (refundError) {
        console.error(`Error refunding participant ${userId}:`, refundError);
      }
    }

    // Mark lesson as cancelled
    await updateDoc(lessonRef, {
      status: 'cancelled',
      cancelledAt: serverTimestamp(),
      participants: [], // Clear participants since they've been refunded
      spotsBooked: 0,
      updatedAt: serverTimestamp(),
    });

    // Send cancellation notifications via n8n (non-blocking)
    (async () => {
      try {
        // Get coach info
        let coachName = '';
        let coachUsername = '';
        let coachEmail = '';
        const coachSnap = await getDoc(doc(db, 'coaches', coachId));
        if (coachSnap.exists()) {
          const coachData2 = coachSnap.data();
          coachName = coachData2.name || coachData2.displayName || '';
          coachUsername = coachData2.username || '';
          coachEmail = coachData2.email || '';

          if (!coachEmail && coachData2.playerProfileId) {
            try {
              const profileSnap = await getDoc(doc(db, 'playerProfiles', coachData2.playerProfileId));
              if (profileSnap.exists()) coachEmail = profileSnap.data().email || '';
            } catch (e) { /* ignore */ }
          }
          if (!coachEmail && coachData2.userId) {
            try {
              const userSnap3 = await getDoc(doc(db, USERS_COLLECTION, coachData2.userId));
              if (userSnap3.exists()) coachEmail = userSnap3.data().email || '';
            } catch (e) { /* ignore */ }
          }
        }

        const functionsUrl = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-chess-web-host.cloudfunctions.net';
        const basePayload = {
          type: 'groupchess_cancellation',
          classType: 'group',
          timestamp: new Date().toISOString(),
          coach: { email: coachEmail, name: coachName, username: coachUsername },
          lesson: {
            id: lessonId,
            title: lessonData.title || '',
            scheduledAtUTC: lessonData.scheduledAtUTC?.toDate?.()
              ? lessonData.scheduledAtUTC.toDate().toISOString()
              : lessonData.scheduledAtUTC || null,
          },
          cancellation: { reason: 'coach_cancelled', creditsRefunded: 1 },
        };

        // Notify each saved participant
        for (const p of savedParticipants) {
          if (!p.email) continue;
          await fetch(`${functionsUrl}/sendCoachingNotification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...basePayload,
              recipientRole: 'student',
              student: { email: p.email, name: p.name || '' },
              calendarUid: `group-${lessonId}-${p.email}`,
            }),
          });
        }

        // Notify coach
        await fetch(`${functionsUrl}/sendCoachingNotification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...basePayload,
            recipientRole: 'coach',
            student: { email: '', name: '' },
            calendarUid: `group-${lessonId}-coach`,
            participants: savedParticipants.map(p => ({ email: p.email, name: p.name || '' })),
          }),
        });
      } catch (err) {
        console.error('Failed to send coach cancellation notifications:', err);
      }
    })();

    return { success: true, refundedCount: refundResults.length };
  } catch (error) {
    console.error('Error cancelling lesson:', error);
    throw error;
  }
};

/**
 * Check if a user is enrolled in a specific lesson
 * @param {string} lessonId - Lesson ID
 * @param {string} userId - User's Firebase UID
 * @returns {Promise<boolean>} - Returns true if enrolled
 */
export const isUserEnrolledInLesson = async (lessonId, userId) => {
  try {
    if (!lessonId || !userId) {
      return false;
    }

    const lesson = await getLesson(lessonId);
    if (!lesson) {
      return false;
    }

    const participants = lesson.participants || [];
    return participants.some(p => p.odaUserId === userId);
  } catch (error) {
    console.error('Error checking enrollment:', error);
    return false;
  }
};

/**
 * Get lessons where user is enrolled (for "My Lessons" tab)
 * @param {string} coachId - Coach ID
 * @param {string} userId - User's Firebase UID
 * @returns {Promise<Array>} - Returns array of lessons where user is participant
 */
export const getEnrolledLessons = async (coachId, userId) => {
  try {
    if (!coachId || !userId) {
      return [];
    }

    // Get all upcoming lessons for this coach
    const lessonsRef = collection(db, LESSONS_COLLECTION);
    const q = query(
      lessonsRef,
      where('coachId', '==', coachId),
      orderBy('scheduledAtUTC', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const enrolledLessons = [];

    querySnapshot.forEach((doc) => {
      const lessonData = doc.data();
      if (lessonData.sessionType === 'individual') return;
      const participants = lessonData.participants || [];

      // Check if user is in participants array
      const isEnrolled = participants.some(p => p.odaUserId === userId);
      if (isEnrolled) {
        enrolledLessons.push({
          id: doc.id,
          ...lessonData
        });
      }
    });

    return enrolledLessons;
  } catch (error) {
    console.error('Error getting enrolled lessons:', error);
    return [];
  }
};

/**
 * Get upcoming lessons where user is NOT enrolled (for "Schedule" tab)
 * @param {string} coachId - Coach ID
 * @param {string} userId - User's Firebase UID
 * @param {number} limitCount - Max number of lessons to return
 * @returns {Promise<Array>} - Returns array of lessons where user is NOT participant
 */
export const getAvailableLessonsForUser = async (coachId, userId, limitCount = 20) => {
  try {
    if (!coachId) {
      return [];
    }

    // Get upcoming lessons for this coach
    const lessonsRef = collection(db, LESSONS_COLLECTION);
    const q = query(
      lessonsRef,
      where('coachId', '==', coachId),
      orderBy('scheduledAtUTC', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const now = new Date();
    const availableLessons = [];

    querySnapshot.forEach((doc) => {
      if (availableLessons.length >= limitCount) return;

      const lessonData = doc.data();

      // Filter to upcoming lessons only
      const scheduledAt = lessonData.scheduledAtUTC?.toDate?.() || new Date(lessonData.scheduledAtUTC);
      if (scheduledAt <= now) return;
      if (lessonData.sessionType === 'individual') return;

      // If userId provided, exclude lessons where user is already enrolled
      if (userId) {
        const participants = lessonData.participants || [];
        const isEnrolled = participants.some(p => p.odaUserId === userId);
        if (isEnrolled) return;
      }

      availableLessons.push({
        id: doc.id,
        ...lessonData
      });
    });

    return availableLessons;
  } catch (error) {
    console.error('Error getting available lessons:', error);
    return [];
  }
};

// ==============================
// Coaching T&C Acceptance
// ==============================

/**
 * Record that a user accepted the coaching terms & conditions.
 * Stores acceptance proof on the user doc (for students) or coach doc (for coaches).
 * @param {string} userId - Firebase UID
 * @param {'student'|'coach'} role - Which role is accepting
 * @param {string} version - T&C version string (e.g. '1.0')
 * @param {string} [coachId] - Coach doc ID (required for coach acceptance)
 */
export const acceptCoachingTerms = async (userId, role, version, coachId = null) => {
  try {
    const acceptance = {
      version,
      acceptedAt: new Date().toISOString(),
    };

    if (role === 'student') {
      const userRef = doc(db, USERS_COLLECTION, userId);
      await setDoc(userRef, {
        coachingTermsAcceptance: acceptance,
      }, { merge: true });
    }

    if (role === 'coach') {
      // Always store on user doc for coach acceptance
      const userRef = doc(db, USERS_COLLECTION, userId);
      await setDoc(userRef, {
        coachingTermsAcceptance: acceptance,
      }, { merge: true });

      // Also store on coach doc if coachId is available
      if (coachId) {
        const coachRef = doc(db, 'coaches', coachId);
        await setDoc(coachRef, {
          coachingTermsAcceptance: acceptance,
        }, { merge: true });
      }
    }

    return acceptance;
  } catch (error) {
    console.error('Error recording coaching terms acceptance:', error);
    throw error;
  }
};

/**
 * Check if a user has accepted the coaching terms.
 * @param {string} userId - Firebase UID
 * @param {'student'|'coach'} role
 * @param {string} requiredVersion - Minimum version required
 * @param {string} [coachId] - Coach doc ID (required for coach role)
 * @returns {Promise<{accepted: boolean, version: string|null, acceptedAt: string|null}>}
 */
export const getCoachingTermsAcceptance = async (userId, role, requiredVersion, coachId = null) => {
  try {
    let acceptance = null;

    if (role === 'student') {
      const userRef = doc(db, USERS_COLLECTION, userId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        acceptance = userSnap.data().coachingTermsAcceptance || null;
      }
    }

    if (role === 'coach') {
      // Check coach doc first if coachId is available
      if (coachId) {
        const coachRef = doc(db, 'coaches', coachId);
        const coachSnap = await getDoc(coachRef);
        if (coachSnap.exists()) {
          acceptance = coachSnap.data().coachingTermsAcceptance || null;
        }
      }
      // Fall back to user doc (acceptance recorded during initial setup)
      if (!acceptance) {
        const userRef = doc(db, USERS_COLLECTION, userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          acceptance = userSnap.data().coachingTermsAcceptance || null;
        }
      }
    }

    if (!acceptance) {
      return { accepted: false, version: null, acceptedAt: null };
    }

    return {
      accepted: acceptance.version >= requiredVersion,
      version: acceptance.version,
      acceptedAt: acceptance.acceptedAt,
    };
  } catch (error) {
    console.error('Error checking coaching terms acceptance:', error);
    return { accepted: false, version: null, acceptedAt: null };
  }
};

// ==============================
// Credit Expiration Helpers
// ==============================

/**
 * Get the credit purchase history for a user-coach pair.
 * Returns individual purchase records with timestamps for expiration tracking.
 * @param {string} userId
 * @param {string} coachId
 * @returns {Promise<Array<{purchasedAt: string, credits: number, expired: boolean}>>}
 */
export const getCreditPurchaseHistory = async (userId, coachId) => {
  try {
    const logsRef = collection(db, 'creditAuditLogs');
    const q = query(
      logsRef,
      where('userId', '==', userId),
      where('coachId', '==', coachId),
      where('type', '==', 'purchase'),
      orderBy('timestamp', 'asc')
    );
    const snap = await getDocs(q);

    const now = new Date();
    const sixMonthsMs = 6 * 30 * 24 * 60 * 60 * 1000; // ~6 months in ms

    return snap.docs.map(d => {
      const data = d.data();
      const purchasedAt = data.purchaseData?.purchasedAt || data.timestamp?.toDate?.()?.toISOString() || data.timestamp;
      const purchaseDate = new Date(purchasedAt);
      const expiresAt = new Date(purchaseDate.getTime() + sixMonthsMs);

      return {
        id: d.id,
        purchasedAt,
        expiresAt: expiresAt.toISOString(),
        credits: data.creditsChanged || 0,
        expired: now > expiresAt,
      };
    });
  } catch (error) {
    console.error('Error getting credit purchase history:', error);
    return [];
  }
};

/**
 * Get the earliest credit expiration date for a user-coach pair.
 * Useful for displaying "credits expire on..." in the dashboard.
 * @param {string} userId
 * @param {string} coachId
 * @returns {Promise<string|null>} ISO date string of earliest expiration, or null
 */
export const getEarliestCreditExpiration = async (userId, coachId) => {
  try {
    const history = await getCreditPurchaseHistory(userId, coachId);
    const nonExpired = history.filter(h => !h.expired);
    if (nonExpired.length === 0) return null;

    // Return the earliest expiration among non-expired purchases
    nonExpired.sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt));
    return nonExpired[0].expiresAt;
  } catch (error) {
    console.error('Error getting earliest credit expiration:', error);
    return null;
  }
};

// ============================================================================
// INDIVIDUAL SESSIONS (1-on-1 Coaching)
// ============================================================================

/**
 * Create an individual session (1-on-1 coaching session day with multiple slots)
 * @param {string} coachId - Coach's Firestore document ID
 * @param {Object} sessionData - Session data
 * @param {string} sessionData.playerProfileId - Coach's player profile ID
 * @param {string} sessionData.title - Session title
 * @param {string} sessionData.date - Date string (YYYY-MM-DD)
 * @param {number} sessionData.priceUsd - Price per slot in USD
 * @param {number} sessionData.slotDuration - Duration of each slot in minutes
 * @param {Array} sessionData.slots - Array of slot objects with startTime (HH:MM format)
 * @param {string} sessionData.coachTimezone - Coach's timezone
 * @param {Object} sessionData.displayTimes - Display times object
 * @returns {Promise<string>} - Returns the created session ID
 */
export const createIndividualSession = async (coachId, sessionData) => {
  try {
    const lessonsRef = collection(db, LESSONS_COLLECTION);
    const sessionDoc = doc(lessonsRef);

    // Initialize slots with "available" status, preserving Daily.co room data if present
    const slots = (sessionData.slots || []).map(slot => ({
      startTime: slot.startTime,
      status: 'available',
      bookedBy: null,
      dailyRoomUrl: slot.dailyRoomUrl || '',
      dailyRoomName: slot.dailyRoomName || '',
    }));

    const dataToSave = {
      sessionType: 'individual',
      coachId,
      playerProfileId: sessionData.playerProfileId || '',
      title: sessionData.title || 'Private Lessons',
      instructor: sessionData.instructor || '',
      date: sessionData.date || '',
      priceUsd: sessionData.priceUsd || 50,
      slotDuration: sessionData.slotDuration || 60,
      slots,
      coachTimezone: sessionData.coachTimezone || '',
      displayTimes: sessionData.displayTimes || {},
      dailyRoomUrl: sessionData.dailyRoomUrl || '',
      dailyRoomName: sessionData.dailyRoomName || '',
      scheduledAtUTC: sessionData.scheduledAtUTC || null,
      autoGenerated: sessionData.autoGenerated || false,
      status: 'scheduled',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(sessionDoc, dataToSave);
    return sessionDoc.id;
  } catch (error) {
    console.error('Error creating individual session:', error);
    throw error;
  }
};

/**
 * Get upcoming individual sessions for a coach
 * @param {string} coachId - Coach ID
 * @param {number} limitCount - Maximum number of sessions to return
 * @returns {Promise<Array>} - Returns array of upcoming individual sessions
 */
export const getUpcomingIndividualSessions = async (coachId, limitCount = 20) => {
  try {
    const lessonsRef = collection(db, LESSONS_COLLECTION);

    const q = query(
      lessonsRef,
      where('coachId', '==', coachId),
      where('sessionType', '==', 'individual'),
      orderBy('date', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const sessions = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Filter: only scheduled sessions with date >= today
      if (data.status === 'scheduled' && data.date >= today) {
        sessions.push({
          id: doc.id,
          ...data
        });
      }
    });

    return sessions.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting upcoming individual sessions:', error);
    throw error;
  }
};

/**
 * Get all individual sessions for a coach (including past)
 * @param {string} coachId - Coach ID
 * @param {number} limitCount - Maximum number of sessions to return
 * @returns {Promise<Array>} - Returns array of individual sessions
 */
export const getAllIndividualSessions = async (coachId, limitCount = 50) => {
  try {
    const lessonsRef = collection(db, LESSONS_COLLECTION);

    const q = query(
      lessonsRef,
      where('coachId', '==', coachId),
      where('sessionType', '==', 'individual'),
      orderBy('date', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const sessions = [];

    querySnapshot.forEach((doc) => {
      sessions.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return sessions.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting all individual sessions:', error);
    throw error;
  }
};

/**
 * Book an individual session slot (atomic transaction)
 * @param {string} lessonId - Session document ID
 * @param {number} slotIndex - Index of the slot to book
 * @param {Object} bookingData - Booking information
 * @param {string} bookingData.odaUserId - User's ID
 * @param {string} bookingData.email - User's email
 * @param {string} bookingData.name - User's name
 * @param {string} bookingData.stripePaymentIntentId - Stripe payment intent ID
 * @returns {Promise<Object>} - Returns booking result
 */
export const bookIndividualSlot = async (lessonId, slotIndex, bookingData) => {
  try {
    const lessonRef = doc(db, LESSONS_COLLECTION, lessonId);

    return await runTransaction(db, async (transaction) => {
      const lessonSnap = await transaction.get(lessonRef);

      if (!lessonSnap.exists()) {
        throw new Error('Session not found');
      }

      const lessonData = lessonSnap.data();

      if (lessonData.sessionType !== 'individual') {
        throw new Error('This is not an individual session');
      }

      const slots = lessonData.slots || [];

      if (slotIndex < 0 || slotIndex >= slots.length) {
        throw new Error('Invalid slot index');
      }

      if (slots[slotIndex].status !== 'available') {
        throw new Error('This slot is no longer available');
      }

      // Update the specific slot
      const updatedSlots = [...slots];
      updatedSlots[slotIndex] = {
        ...updatedSlots[slotIndex],
        status: 'booked',
        bookedBy: {
          odaUserId: bookingData.odaUserId,
          email: bookingData.email,
          name: bookingData.name,
          bookedAt: new Date().toISOString(),
          stripePaymentIntentId: bookingData.stripePaymentIntentId || null
        }
      };

      transaction.update(lessonRef, {
        slots: updatedSlots,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        slot: updatedSlots[slotIndex]
      };
    });
  } catch (error) {
    console.error('Error booking individual slot:', error);
    throw error;
  }
};

/**
 * Check if a student has ever had a lesson with a specific coach.
 * Checks both group lessons (spots array) and individual sessions (slots.bookedBy).
 * @param {string} studentUserId - Student's Firebase UID
 * @param {string} coachId - Coach's Firestore document ID
 * @returns {Promise<boolean>} - true if this is the student's first lesson with this coach
 */
export const isFirstLessonWithCoach = async (studentUserId, coachId) => {
  try {
    const lessonsRef = collection(db, LESSONS_COLLECTION);

    // Check individual sessions — any slot booked by this student for this coach
    const individualQuery = query(
      lessonsRef,
      where('coachId', '==', coachId),
      where('sessionType', '==', 'individual')
    );
    const individualSnap = await getDocs(individualQuery);
    for (const docSnap of individualSnap.docs) {
      const slots = docSnap.data().slots || [];
      for (const slot of slots) {
        if (slot.bookedBy?.odaUserId === studentUserId) {
          return false; // Has a prior individual booking
        }
      }
    }

    // Check group lessons — spots array contains this student
    const groupQuery = query(
      lessonsRef,
      where('coachId', '==', coachId),
      where('sessionType', '==', 'group')
    );
    const groupSnap = await getDocs(groupQuery);
    for (const docSnap of groupSnap.docs) {
      const spots = docSnap.data().spots || [];
      for (const spot of spots) {
        if (spot.odaUserId === studentUserId || spot.userId === studentUserId) {
          return false; // Has a prior group lesson
        }
      }
    }

    // Also check the coach's students subcollection for credit purchases
    const studentDoc = await getDoc(doc(db, 'coaches', coachId, 'students', studentUserId));
    if (studentDoc.exists()) {
      const data = studentDoc.data();
      if (data.lessonsAttended > 0 || data.firstPurchaseAt) {
        return false;
      }
    }

    return true; // No prior lessons found
  } catch (error) {
    console.error('Error checking first lesson status:', error);
    return true; // Default to first lesson (apply higher fee) on error
  }
};

/**
 * Cancel an individual session slot booking
 * @param {string} lessonId - Session document ID
 * @param {number} slotIndex - Index of the slot to cancel
 * @param {string} userId - User's ID (must match bookedBy.odaUserId)
 * @param {boolean} bypassTimeCheck - Skip 4-hour cancellation window check (for refunds)
 * @returns {Promise<Object>} - Returns cancellation result including stripePaymentIntentId for refund
 */
export const cancelIndividualSlot = async (lessonId, slotIndex, userId, bypassTimeCheck = false) => {
  try {
    const lessonRef = doc(db, LESSONS_COLLECTION, lessonId);

    return await runTransaction(db, async (transaction) => {
      const lessonSnap = await transaction.get(lessonRef);

      if (!lessonSnap.exists()) {
        throw new Error('Session not found');
      }

      const lessonData = lessonSnap.data();

      if (lessonData.sessionType !== 'individual') {
        throw new Error('This is not an individual session');
      }

      const slots = lessonData.slots || [];

      if (slotIndex < 0 || slotIndex >= slots.length) {
        throw new Error('Invalid slot index');
      }

      const slot = slots[slotIndex];

      if (slot.status !== 'booked') {
        throw new Error('This slot is not booked');
      }

      if (slot.bookedBy?.odaUserId !== userId) {
        throw new Error('You can only cancel your own bookings');
      }

      // Check 4-hour cancellation window
      if (!bypassTimeCheck) {
        const sessionDate = lessonData.date; // YYYY-MM-DD
        const slotTime = slot.startTime; // HH:MM
        const timezone = lessonData.coachTimezone || 'UTC';

        // Create date object for the slot start time
        const slotDateTime = new Date(`${sessionDate}T${slotTime}:00`);
        const now = new Date();
        const fourHoursMs = 4 * 60 * 60 * 1000;

        if (slotDateTime.getTime() - now.getTime() < fourHoursMs) {
          throw new Error('Cannot cancel less than 4 hours before the session start time');
        }
      }

      const stripePaymentIntentId = slot.bookedBy?.stripePaymentIntentId;

      // Reset the slot to available
      const updatedSlots = [...slots];
      updatedSlots[slotIndex] = {
        startTime: slot.startTime,
        status: 'available',
        bookedBy: null
      };

      transaction.update(lessonRef, {
        slots: updatedSlots,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        stripePaymentIntentId,
        refundRequired: !!stripePaymentIntentId
      };
    });
  } catch (error) {
    console.error('Error cancelling individual slot:', error);
    throw error;
  }
};

/**
 * Delete an individual session (only if no slots are booked)
 * @param {string} lessonId - Session document ID
 * @returns {Promise<void>}
 */
export const deleteIndividualSession = async (lessonId) => {
  try {
    const lessonRef = doc(db, LESSONS_COLLECTION, lessonId);
    const lessonSnap = await getDoc(lessonRef);

    if (!lessonSnap.exists()) {
      throw new Error('Session not found');
    }

    const lessonData = lessonSnap.data();

    if (lessonData.sessionType !== 'individual') {
      throw new Error('This is not an individual session');
    }

    // Check if any slots are booked
    const hasBookings = (lessonData.slots || []).some(slot => slot.status === 'booked');

    if (hasBookings) {
      throw new Error('Cannot delete session with existing bookings. Cancel all bookings first.');
    }

    await deleteDoc(lessonRef);
  } catch (error) {
    console.error('Error deleting individual session:', error);
    throw error;
  }
};

/**
 * Remove a single unbooked slot from an individual session.
 * If it was the last slot, deletes the entire session document.
 */
export const removeIndividualSessionSlot = async (lessonId, slotIndex) => {
  const lessonRef = doc(db, LESSONS_COLLECTION, lessonId);
  const lessonSnap = await getDoc(lessonRef);

  if (!lessonSnap.exists()) throw new Error('Session not found');

  const data = lessonSnap.data();
  if (data.sessionType !== 'individual') throw new Error('Not an individual session');

  const slots = data.slots || [];
  if (slotIndex < 0 || slotIndex >= slots.length) throw new Error('Invalid slot index');
  if (slots[slotIndex].status === 'booked') throw new Error('Cannot delete a booked slot');

  const updatedSlots = slots.filter((_, i) => i !== slotIndex);

  if (updatedSlots.length === 0) {
    await deleteDoc(lessonRef);
  } else {
    await updateDoc(lessonRef, { slots: updatedSlots });
  }
};

/**
 * Get user's booked individual sessions
 * @param {string} coachId - Coach ID
 * @param {string} userId - User's ID
 * @returns {Promise<Array>} - Returns array of sessions with user's booked slots
 */
export const getUserBookedIndividualSessions = async (coachId, userId) => {
  try {
    const lessonsRef = collection(db, LESSONS_COLLECTION);

    const q = query(
      lessonsRef,
      where('coachId', '==', coachId),
      where('sessionType', '==', 'individual'),
      orderBy('date', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const bookedSessions = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const slots = data.slots || [];

      // Find slots booked by this user
      slots.forEach((slot, index) => {
        if (slot.status === 'booked' && slot.bookedBy?.odaUserId === userId) {
          bookedSessions.push({
            id: doc.id,
            slotIndex: index,
            ...data,
            bookedSlot: slot
          });
        }
      });
    });

    return bookedSessions;
  } catch (error) {
    console.error('Error getting user booked individual sessions:', error);
    throw error;
  }
};

/**
 * Check if user can cancel an individual session booking (4-hour rule)
 * @param {string} lessonId - Session document ID
 * @param {number} slotIndex - Index of the slot
 * @returns {Promise<Object>} - Returns { canCancel, reason, minutesUntilDeadline }
 */
export const canCancelIndividualSlot = async (lessonId, slotIndex) => {
  try {
    const lesson = await getLesson(lessonId);

    if (!lesson || lesson.sessionType !== 'individual') {
      return { canCancel: false, reason: 'Session not found' };
    }

    const slot = lesson.slots?.[slotIndex];
    if (!slot || slot.status !== 'booked') {
      return { canCancel: false, reason: 'Slot not booked' };
    }

    const sessionDate = lesson.date; // YYYY-MM-DD
    const slotTime = slot.startTime; // HH:MM
    const slotDateTime = new Date(`${sessionDate}T${slotTime}:00`);
    const now = new Date();
    const fourHoursMs = 4 * 60 * 60 * 1000;
    const timeUntilSession = slotDateTime.getTime() - now.getTime();

    if (timeUntilSession < fourHoursMs) {
      const minutesUntilDeadline = Math.max(0, Math.floor(timeUntilSession / (60 * 1000)));
      return {
        canCancel: false,
        reason: 'Less than 4 hours before session',
        minutesUntilDeadline
      };
    }

    return {
      canCancel: true,
      reason: null,
      minutesUntilDeadline: Math.floor((timeUntilSession - fourHoursMs) / (60 * 1000))
    };
  } catch (error) {
    console.error('Error checking cancel eligibility:', error);
    return { canCancel: false, reason: error.message };
  }
};

// Export all functions as a service object (optional)
/**
 * Update user onboarding intent and related fields
 * Used during new account onboarding to save selected intents
 */
export const updateUserOnboardingIntent = async (userId, data) => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, data, { merge: true });
  } catch (error) {
    console.error('Error updating user onboarding intent:', error);
    throw error;
  }
};

export const firestoreService = {
  // Event functions
  createEvent,
  createEventWithId,
  getEvent,
  getEvents,
  getAllEvents,
  updateEvent,
  publishEvent,
  eventExists,
  duplicateEvent,
  // User functions
  createUserProfile,
  getUserProfile,
  getUserEventCount,
  updateUserProfile,
  adminGrantPremium,
  adminRevokePremium,
  // Event URL functions
  isCustomUrlAvailable,
  createCustomUrl,
  getEventIdFromCustomUrl,
  getCustomUrlsByOrganizer,
  getCustomUrlsByEventId,
  deleteCustomUrl,
  // Player profile functions
  createPlayerProfile,
  createPlayerProfileWithId,
  getPlayerProfile,
  getPlayerProfilesByUser,
  getUserPlayerProfileCount,
  updatePlayerProfile,
  publishPlayerProfile,
  deletePlayerProfile,
  // Player URL functions
  createPlayerUrl,
  getProfileIdFromUsername,
  getPlayerUrlsByUser,
  deletePlayerUrl,
  // Debug functions
  debugGetAllCustomUrls,
  // Chess game functions
  getChessGame,
  updateChessGame,
  resetChessGame,
  // Owner2/Co-owner functions
  getUserEventCountWithShared,
  getUserPlayerProfileCountWithShared,
  getUserEventsWithShared,
  getUserPlayerProfilesWithShared,
  canEditEvent,
  canEditPlayerProfile,
  addEventCoOwner,
  removeEventCoOwner,
  addPlayerProfileCoOwner,
  removePlayerProfileCoOwner,
  getUserByEmail,
  transferPlayerProfileOwnership,
  // Referral functions
  handleReferralSignup,
  grantReferralRewards,
  handleReferralConversion,
  markProfileAsReferred,
  getReferralStats,
  getReferralEvents,
  getAllReferralStats,
  getAllReferralEvents,
  // Blog functions
  createBlog,
  getBlogsByProfileId,
  getPublishedBlogsByProfileId,
  getAllPublishedBlogs,
  getBlogById,
  getBlogByUsernameAndSlug,
  updateBlog,
  deleteBlog,
  pinBlog,
  unpinBlog,
  generateSlug,
  generateExcerpt,
  // Products functions
  getAllActiveProducts,
  // Direct Message functions
  submitDirectMessage,
  getDirectMessages,
  markMessageAsRead,
  checkDMRateLimit,
  // Email Subscriber functions
  addEmailSubscriber,
  getEmailSubscribers,
  unsubscribeEmail,
  setEmailUnsubscribed,
  deleteEmailSubscriber,
  getSubscriberByEmail,
  // Payment Event Logs functions
  createPaymentEventLog,
  getPaymentEventLogs,
  searchPaymentEventLogs,
  // Lichess Integration functions
  saveLichessToken,
  getLichessToken,
  removeLichessConnection,
  // Coach functions
  createCoachProfile,
  getCoach,
  getCoachByPlayerProfile,
  updateCoach,
  getAllCoaches,
  // PayPal coach functions
  setCoachPayPalEmail,
  getCoachPayPalRevenue,
  getCoachPayPalInvoices,
  getAllPayPalInvoices,
  getPayPalTransactionsByIds,
  // Lesson functions
  createLesson,
  getLesson,
  getLessons,
  getUpcomingLessons,
  getPastLessons,
  updateLesson,
  cancelLesson,
  deleteLesson,
  bookLessonSpot,
  getAvailableLessonSpots,
  // Student registration functions
  getUserCoachCredits,
  addUserCoachCredits,
  deductUserCoachCredits,
  refundUserCoachCredits,
  createCreditAuditLog,
  getCoachEarnings,
  getCoachStudents,
  registerForLesson,
  cancelLessonRegistration,
  coachCancelLesson,
  isUserEnrolledInLesson,
  getEnrolledLessons,
  getAvailableLessonsForUser,
  // Coaching T&C functions
  acceptCoachingTerms,
  getCoachingTermsAcceptance,
  // Credit expiration functions
  getCreditPurchaseHistory,
  getEarliestCreditExpiration,
  // Individual session (1-on-1) functions
  createIndividualSession,
  getUpcomingIndividualSessions,
  getAllIndividualSessions,
  bookIndividualSlot,
  cancelIndividualSlot,
  deleteIndividualSession,
  removeIndividualSessionSlot,
  getUserBookedIndividualSessions,
  canCancelIndividualSlot,
};
